import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { inr, apiErr, computeWeights } from "@/lib/api";
import { DashShell, KPI, StatusTag } from "@/components/dash";
import { toast } from "sonner";
import { LayoutDashboard, Package, Upload, UploadCloud, Wallet, Banknote, ShieldCheck, Loader2, TrendingUp, Trash2, Sparkles, Check, Image as ImageIcon, RefreshCw, ChevronUp, ChevronDown, Pencil, UserCog } from "lucide-react";
import EditListingModal from "@/components/EditListingModal";
import ProfilePanel from "@/components/ProfilePanel";
import FaqBlock, { FAQ_SLUGS } from "@/components/FaqBlock";
import PhotoManager from "@/components/PhotoManager";
import ClientStudioRenderer from "@/components/ClientStudioRenderer";
import RenderProgressPanel from "@/components/RenderProgressPanel";
import EncourageOwnRenderModal from "@/components/EncourageOwnRenderModal";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { logTermsCheckpoint } from "@/lib/termsCheckpoint";
import { Link } from "react-router-dom";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "listings", label: "My Listings", icon: Package },
  { key: "upload", label: "Upload Design", icon: Upload },
  { key: "bulk", label: "Bulk Upload", icon: UploadCloud },
  { key: "wallet", label: "Wallet", icon: Wallet },
  { key: "payouts", label: "Payouts", icon: Banknote },
  { key: "kyc", label: "Verification", icon: ShieldCheck },
  { key: "profile", label: "Profile", icon: UserCog },
];

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "quarter", label: "This Quarter" },
  { key: "year", label: "Yearly" },
  { key: "all", label: "All time" },
];

export default function SellerDashboard() {
  const [params, setParams] = useSearchParams();
  const active = params.get("tab") || "overview";
  const setTab = (t) => setParams({ tab: t });
  const [analytics, setAnalytics] = useState(null);
  const [overview, setOverview] = useState(null);
  const [period, setPeriod] = useState("month");
  const [wallet, setWallet] = useState(null);
  const [listings, setListings] = useState([]);
  const [sales, setSales] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [cats, setCats] = useState([]);
  // PhotoManager modal — { listing } when open, null when closed.
  const [photoTarget, setPhotoTarget] = useState(null);
  // Edit-listing modal — { listing } when open (name/price/description +
  // CAD-file replace). File swap is blocked once the design has sales.
  const [editTarget, setEditTarget] = useState(null);
  // 2026-03-01 — suspension state (repeat-offender KYC freeze). When
  // suspended=true, we surface a red banner + an appeal form so the
  // seller isn't sent to "email support" as a dead-end.
  const [suspension, setSuspension] = useState(null);
  const [showAppealModal, setShowAppealModal] = useState(false);

  const load = () => {
    api.get("/seller/analytics").then((r) => setAnalytics(r.data));
    api.get("/seller/wallet").then((r) => setWallet(r.data));
    api.get("/seller/listings").then((r) => setListings(r.data));
    api.get("/seller/sales").then((r) => setSales(r.data));
    api.get("/seller/payouts").then((r) => setPayouts(r.data));
    api.get("/categories").then((r) => setCats(r.data.filter((c) => !c.parent_id)));
    api.get("/seller/suspension").then((r) => setSuspension(r.data)).catch(() => setSuspension(null));
  };
  useEffect(() => { load(); }, []);
  // Refetch the period-scoped overview whenever the selected window changes.
  useEffect(() => {
    api.get(`/seller/overview?period=${period}`).then((r) => setOverview(r.data)).catch(() => setOverview(null));
  }, [period]);

  const del = async (id) => {
    if (!window.confirm("Delete this design? Files that already have sales are removed from the marketplace but kept for buyers' downloads.")) return;
    try {
      const { data } = await api.delete(`/seller/listings/${id}`);
      toast.success(data.removed ? "Removed from marketplace (design has sales)" : "Design deleted");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <DashShell title="Designer Studio" subtitle="Listings, earnings & payouts" nav={NAV} active={active} onTab={setTab}>
      {/* 2026-03-01 — Suspension banner (repeat-offender KYC freeze).
          Red, non-dismissible, opens the self-service appeal form. */}
      {suspension?.suspended && (
        <div data-testid="dash-suspension-banner" className="mb-4 border-2 border-red-400 bg-red-50 text-red-900 rounded-md p-4 flex items-start gap-3 shadow-sm">
          <ShieldCheck className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-700" />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-base mb-1">Your seller account is under review</p>
            <p className="mb-2">
              {suspension.fraud_flag?.reason === "cad_mismatch_repeat_offender"
                ? `Your account was automatically suspended after ${suspension.fraud_flag.count_30d} listings were delisted in the last 30 days because the uploaded photos didn't match your CAD file.`
                : "Your account has been suspended by our team."}
              {" "}New uploads and payouts are blocked until we review your account. Existing orders remain deliverable to their buyers.
            </p>
            {suspension.latest_appeal?.status === "pending" && (
              <p className="text-xs italic text-red-800">
                Your appeal from {new Date(suspension.latest_appeal.created_at).toLocaleString()} is under review — we'll get back to you within 2 business days.
              </p>
            )}
            {suspension.latest_appeal?.status === "rejected" && (
              <p className="text-xs italic text-red-800">
                Your last appeal was rejected on {new Date(suspension.latest_appeal.resolved_at).toLocaleString()}. {suspension.latest_appeal.resolution_note || ""}
              </p>
            )}
          </div>
          {suspension.latest_appeal?.status !== "pending" && (
            <button
              data-testid="dash-appeal-cta"
              onClick={() => setShowAppealModal(true)}
              className="bg-red-800 text-white rounded-md px-4 py-2 text-xs font-semibold hover:bg-red-700 whitespace-nowrap">
              File an appeal →
            </button>
          )}
        </div>
      )}
      {showAppealModal && (
        <AppealModal
          suspension={suspension}
          onClose={() => setShowAppealModal(false)}
          onSubmitted={() => { setShowAppealModal(false); load(); }}
        />
      )}
      {/* Persistent dashboard-wide KYC banner — non-dismissible while
          unverified. Shows the exact ₹ available to withdraw, because that
          concrete number is what actually motivates KYC completion. */}
      {wallet && !wallet.can_withdraw && wallet.blocking_reason?.startsWith("kyc_") && (
        <div data-testid="dash-kyc-banner" className="mb-4 border border-amber-300 bg-amber-50 text-amber-900 rounded-md p-3 flex items-center gap-3 shadow-sm">
          <ShieldCheck className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <span className="font-semibold">{inr(wallet.available || 0)} available.</span>{" "}
            Complete KYC to withdraw your earnings. Nothing is lost while you're unverified — money continues to accrue.
          </div>
          <button
            data-testid="dash-kyc-cta"
            onClick={() => setTab("kyc")}
            className="bg-amber-900 text-white rounded-md px-4 py-1.5 text-xs font-semibold hover:bg-amber-800 whitespace-nowrap">
            Complete KYC →
          </button>
        </div>
      )}      {active === "overview" && (
        <div className="space-y-5">
          {/* --- Period filter tabs --- */}
          <div className="flex flex-wrap gap-2" data-testid="seller-period-tabs">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                data-testid={`period-${p.key}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${period === p.key ? "bg-primary text-primary-foreground border-primary shadow-sm" : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary"}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {!overview && (
            <div className="border border-dashed border-border rounded-md py-14 text-center text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading your numbers…
            </div>
          )}

          {overview && (
            <>
              {/* --- Revenue breakdown card --- */}
              <div className="border border-border rounded-md bg-card p-5 sm:p-6" data-testid="seller-revenue-breakdown">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Earnings breakdown</p>
                    <h3 className="font-display font-bold text-lg mt-1">{PERIODS.find((p) => p.key === period)?.label}</h3>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Total sales</p>
                    <p className="font-display font-bold text-2xl">{overview.total_sales}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <BreakdownStat label="Gross Revenue" value={overview.gross_revenue} tint="text-slate-800" testid="stat-gross" />
                  <BreakdownStat label={`Platform Fee (${overview.commission_pct}%)`} value={-overview.platform_commission} tint="text-red-500" testid="stat-commission" />
                  <BreakdownStat label={`Gateway Fee (${overview.gateway_pct}%)`} value={-overview.gateway_fee} tint="text-red-500" testid="stat-gateway" />
                  <BreakdownStat label="Net Earnings" value={overview.net_earnings} tint="text-primary" bold testid="stat-net" />
                </div>
                <p className="text-[11px] text-muted-foreground font-mono mt-4 leading-relaxed border-t border-border pt-3">
                  Gross − Platform Commission − Payment Gateway Fee = Net Earnings.
                  Amounts marked in red are automatically deducted before the payout hits your wallet.
                </p>
              </div>

              {/* --- Secondary KPIs --- */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <KPI label="Total Views" value={overview.total_views} />
                <KPI label="Active Listings" value={overview.active_listings} />
                <KPI label="Wallet (available)" value={inr(wallet?.available || 0)} accent="text-primary" />
              </div>

              <div className="grid lg:grid-cols-2 gap-4">
                {/* --- Top Selling Design card --- */}
                <div className="border border-border rounded-md bg-card p-4 sm:p-5" data-testid="seller-top-design">
                  <p className="font-display font-semibold text-sm mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> Top-selling design</p>
                  {overview.top_listing ? (
                    <Link to={`/design/${overview.top_listing.id}`} className="flex gap-3 group">
                      <div className="relative w-24 h-24 sm:w-32 sm:h-32 shrink-0 rounded-md overflow-hidden bg-slate-900">
                        {overview.top_listing.cover_image ? (
                          <img src={overview.top_listing.cover_image} alt={overview.top_listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">no cover</div>
                        )}
                        <span className="absolute top-1 left-1 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded text-slate-900" style={{ backgroundColor: "hsl(var(--gold))" }}>#1</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{overview.top_listing.category_name || "—"}</p>
                        <p className="font-display font-semibold text-base mt-0.5 leading-tight group-hover:text-primary transition-colors line-clamp-2">
                          {overview.top_listing.title}
                        </p>
                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <p className="font-mono uppercase tracking-wider text-muted-foreground text-[10px]">Units sold</p>
                            <p className="font-display font-bold text-lg">{overview.top_listing.units_sold}</p>
                          </div>
                          <div>
                            <p className="font-mono uppercase tracking-wider text-muted-foreground text-[10px]">Revenue (net)</p>
                            <p className="font-display font-bold text-lg text-primary">{inr(overview.top_listing.revenue)}</p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <div className="text-sm text-muted-foreground py-6 text-center">No sales in this period yet.</div>
                  )}
                </div>

                {/* --- Revenue trend --- */}
                <div className="border border-border rounded-md bg-card p-4 sm:p-5">
                  <p className="font-display font-semibold text-sm mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> Revenue trend</p>
                  {overview.chart.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-6 text-center">No data in this window.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={overview.chart}>
                        <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.4} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient></defs>
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v) => `₹${v}`} /><Area type="monotone" dataKey="revenue" stroke="#2563EB" fill="url(#rev)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Contextual FAQs — payments cluster for sellers */}
          <FaqBlock title="Payments — how you get paid" slugs={FAQ_SLUGS.payments} testid="seller-faq-payments" />
        </div>
      )}

      {active === "listings" && (
        <div className="border border-border rounded-md bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left"><tr>{["Design", "Format", "Price", "Views", "Sales", "Status", ""].map((h) => <th key={h} className="px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{h}</th>)}</tr></thead>
            <tbody>
              {listings.map((l) => (
                <tr key={l.id} className="border-t border-border hover:bg-secondary/30">
                  <td className="px-4 py-3 font-medium flex items-center gap-2">
                    {l.cover_image && <img src={l.cover_image} alt="" className="h-8 w-8 rounded object-cover border border-border bg-white" />}
                    <span className="truncate">{l.title}</span>
                  </td>
                  <td className="px-4 py-3 font-mono uppercase">{l.file_type}</td>
                  <td className="px-4 py-3 font-mono">{inr(l.price)}</td>
                  <td className="px-4 py-3 font-mono">{l.views}</td>
                  <td className="px-4 py-3 font-mono">{l.sales_count}</td>
                  <td className="px-4 py-3"><StatusTag status={l.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {/* 2026-02 seller-photo mandate — every listing held
                          at needs_photos surfaces a green "Continue setup"
                          button that reopens the PhotoManager so the seller
                          can finish uploading their own render + picking
                          a cover. No draft ever quietly rots. */}
                      {l.status === "needs_photos" ? (
                        <button
                          onClick={() => setPhotoTarget(l)}
                          data-testid={`continue-setup-${l.id}`}
                          title="Finish setting up this design — upload a render or pick a cover"
                          className="inline-flex items-center gap-1 bg-emerald-600 text-white rounded-md px-2 py-1 text-[11px] font-bold hover:bg-emerald-700"
                        >
                          Continue setup
                        </button>
                      ) : (
                        <button
                          onClick={() => setPhotoTarget(l)}
                          data-testid={`photos-listing-${l.id}`}
                          title="Manage photos"
                          className="text-primary hover:bg-primary/10 rounded p-1"
                        >
                          <ImageIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setEditTarget(l)}
                        data-testid={`edit-listing-${l.id}`}
                        title="Edit name, price, description or replace the CAD file"
                        className="inline-flex items-center gap-1 border border-border rounded-md px-2 py-1 text-[11px] font-semibold hover:border-primary hover:text-primary"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                      <button onClick={() => del(l.id)} data-testid={`delete-listing-${l.id}`} title="Delete design" className="text-red-500 hover:text-red-600 p-1"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {listings.length === 0 && <tr><td colSpan={7} className="px-4 py-16 text-center text-muted-foreground">No listings yet. Upload your first design.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {active === "upload" && <UploadForm cats={cats} onDone={(row) => {
        // 2026-02 forced-cover flow — after render finishes, the seller
        // has to (a) optionally upload their own render and (b) pick a
        // cover before the listing goes live. `_save_listing` returns
        // the listing pinned at status=needs_photos; we auto-open the
        // PhotoManager on it so the seller lands directly on the
        // Continue-setup screen instead of a misleading "Listed
        // successfully" that hid the outstanding steps.
        load();
        setTab("listings");
        if (row && row.id) {
          // Give the listings load() a beat, then open PhotoManager.
          setTimeout(() => setPhotoTarget({ id: row.id, title: row.title || "" }), 300);
        }
      }} />}

      {active === "bulk" && <BulkUploadForm cats={cats} onDone={() => { load(); setTab("listings"); }} onOpenPhotos={(listing) => setPhotoTarget(listing)} />}

      {active === "wallet" && wallet && (
        <SellerWalletTab wallet={wallet} />
      )}

      {active === "payouts" && <PayoutTab wallet={wallet} payouts={payouts} onDone={(nextTab) => {
        load();
        if (typeof nextTab === "string") setTab(nextTab);
      }} />}

      {active === "kyc" && <KycTab onDone={load} />}

      {active === "profile" && <ProfilePanel />}

      {/* Photo manager modal — opens from any listing row + auto after upload */}
      {photoTarget && (
        <PhotoManager
          listing={photoTarget}
          onClose={() => setPhotoTarget(null)}
          onSaved={() => { setPhotoTarget(null); load(); }}
        />
      )}

      {editTarget && (
        <EditListingModal
          listing={editTarget}
          cats={cats}
          onClose={() => setEditTarget(null)}
          onSaved={(res) => {
            load();
            // A CAD swap wipes the gallery — drop the seller straight into
            // the Studio-photos modal so the browser renders the new mesh.
            if (res?.replaced && res.listing) {
              setTimeout(() => setPhotoTarget({ id: res.listing.id, title: res.listing.title }), 350);
            }
          }}
        />
      )}
    </DashShell>
  );
}

function BreakdownStat({ label, value, tint = "text-slate-800", bold = false, testid }) {
  const negative = value < 0;
  return (
    <div className="rounded-md border border-border bg-background p-3" data-testid={testid}>
      <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 font-display ${bold ? "text-2xl font-extrabold" : "text-xl font-bold"} ${tint} leading-none`}>
        {negative ? "−" : ""}{inr(Math.abs(value))}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// SellerWalletTab — 3-sub-tab bank-statement view (Statement / Sales / Payouts)
// spec: bank-statement style, newest first, expandable rows, IST, mobile-first
// ─────────────────────────────────────────────────────────────────────
function SellerWalletTab({ wallet }) {
  const [sub, setSub] = useState("statement");
  return (
    <div className="space-y-5">
      {/* KPI row — spec §1 */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <KPI label="Available" value={inr(wallet.available)} sub="ready to withdraw" accent="text-emerald-500" />
        <KPI label="Pending" value={inr(wallet.pending)} sub="in 48h hold" accent="text-amber-500" />
        <KPI label="On hold" value={inr(wallet.on_hold)} sub="dispute" accent="text-red-500" />
        <KPI label="Total earned" value={inr(wallet.total_earned)} sub="lifetime" />
        <KPI label="Total withdrawn" value={inr(wallet.total_withdrawn || 0)} sub="lifetime" />
        <KPI
          label="Last payout"
          value={wallet.last_payout?.paid_at ? new Date(wallet.last_payout.paid_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }) : "—"}
          sub={wallet.last_payout?.utr ? `UTR ${wallet.last_payout.utr.slice(0, 10)}…` : "no payouts yet"}
          accent="text-primary"
        />
      </div>
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {[
          ["statement", "Statement"],
          ["sales", "Sales"],
          ["payouts", "Payouts"],
        ].map(([k, label]) => (
          <button key={k} data-testid={`wallet-subtab-${k}`} onClick={() => setSub(k)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors whitespace-nowrap ${
              sub === k ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >{label}</button>
        ))}
      </div>
      {sub === "statement" && <StatementView />}
      {sub === "sales" && <SalesView />}
      {sub === "payouts" && <PayoutsView />}
    </div>
  );
}

function _fmtIST(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata",
    });
  } catch { return iso; }
}
function _copy(v) { if (!v) return; navigator.clipboard.writeText(v); toast.success("Copied"); }

function StatementView() {
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  useEffect(() => { api.get("/seller/statement").then((r) => setRows(r.data)).catch(() => {}); }, []);
  const filtered = rows.filter((r) => {
    if (kind !== "all" && r.kind !== kind) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.description || "").toLowerCase().includes(s)
      || (r.utr || "").toLowerCase().includes(s)
      || (r.reference || "").toLowerCase().includes(s);
  });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="stmt-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search design or UTR…" className="border border-border rounded px-3 py-1.5 text-sm w-full md:w-64" />
        <div className="flex gap-1 text-xs">
          {["all", "sale", "payout", "refund"].map((k) => (
            <button key={k} data-testid={`stmt-filter-${k}`} onClick={() => setKind(k)}
              className={`px-3 py-1.5 rounded ${kind === k ? "bg-primary text-primary-foreground" : "border border-border"}`}>
              {k[0].toUpperCase() + k.slice(1)}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} row{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block border border-border rounded-md bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left">
            <tr>{["Date", "Description", "Credit", "Debit", "Balance"].map((h) => (
              <th key={h} className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <React.Fragment key={r.id}>
                <tr onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    data-testid={`stmt-row-${r.id}`}
                    className="border-t border-border cursor-pointer hover:bg-secondary/30">
                  <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">{_fmtIST(r.when)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      {r.kind === "payout" && <Banknote className="w-3.5 h-3.5 text-blue-600" />}
                      {r.kind === "sale" && <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />}
                      {r.kind === "refund" && <RefreshCw className="w-3.5 h-3.5 text-red-600" />}
                      <span className="font-medium">{r.description}</span>
                      {r.utr && (
                        <button onClick={(e) => { e.stopPropagation(); _copy(r.utr); }} className="ml-1 text-[10px] text-primary underline">copy UTR</button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-emerald-600">{r.credit ? "+" + inr(r.credit) : ""}</td>
                  <td className="px-4 py-2.5 font-mono text-red-600">{r.debit ? "−" + inr(r.debit) : ""}</td>
                  <td className="px-4 py-2.5 font-mono font-semibold">{inr(r.balance)}</td>
                </tr>
                {expanded === r.id && (
                  <tr className="bg-secondary/40">
                    <td colSpan={5} className="px-6 py-3">
                      <StatementBreakdown row={r} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-16 text-center text-muted-foreground">No matching entries.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {filtered.map((r) => (
          <div key={r.id} data-testid={`stmt-card-${r.id}`}
               onClick={() => setExpanded(expanded === r.id ? null : r.id)}
               className="border border-border rounded-md bg-card p-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="font-mono">{_fmtIST(r.when)}</span>
              <span className={r.credit ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>
                {r.credit ? "+" + inr(r.credit) : "−" + inr(r.debit)}
              </span>
            </div>
            <div className="mt-1 font-medium text-sm">{r.description}</div>
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Balance</span>
              <span className="font-mono">{inr(r.balance)}</span>
            </div>
            {expanded === r.id && <div className="mt-3 pt-3 border-t border-border"><StatementBreakdown row={r} /></div>}
          </div>
        ))}
        {filtered.length === 0 && <div className="border border-dashed border-border rounded-md p-8 text-center text-muted-foreground text-sm">No entries.</div>}
      </div>
    </div>
  );
}

function StatementBreakdown({ row }) {
  const b = row.breakdown || {};
  if (row.kind === "payout") {
    return (
      <div className="grid grid-cols-2 gap-x-8 gap-y-1 font-mono text-xs">
        <span className="text-muted-foreground">Amount paid</span><span className="text-right">{inr(b.gross)}</span>
        {b.tds > 0 && <><span className="text-muted-foreground">TDS deducted</span><span className="text-right">−{inr(b.tds)}</span></>}
        <span className="text-muted-foreground">Net transferred</span><span className="text-right font-semibold">{inr(b.net || b.gross)}</span>
        <span className="text-muted-foreground">Method</span><span className="text-right uppercase">{b.method}</span>
        <span className="text-muted-foreground">Destination</span><span className="text-right">{b.destination || "—"}</span>
        <span className="text-muted-foreground">UTR</span><span className="text-right select-all">{b.utr || "—"}</span>
        <span className="text-muted-foreground">Paid at</span><span className="text-right">{_fmtIST(b.paid_at)}</span>
        {b.receipt_id && <><span className="text-muted-foreground">Receipt #</span><span className="text-right">{b.receipt_id}</span></>}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-1 font-mono text-xs">
      <span className="text-muted-foreground">Buyer paid</span><span className="text-right">{inr(b.gross)}</span>
      <span className="text-muted-foreground">Platform commission</span><span className="text-right text-red-600">−{inr(b.commission)}</span>
      {b.gateway > 0 && <><span className="text-muted-foreground">Gateway charges</span><span className="text-right text-red-600">−{inr(b.gateway)}</span></>}
      {b.tds > 0 && <><span className="text-muted-foreground">TDS (1%)</span><span className="text-right text-red-600">−{inr(b.tds)}</span></>}
      <span className="text-muted-foreground border-t border-border pt-1 mt-1">Credited to wallet</span><span className="text-right font-bold border-t border-border pt-1 mt-1">{row.kind === "refund" ? "−" : ""}{inr(Math.abs(b.net))}</span>
      <span className="text-muted-foreground">Sold</span><span className="text-right">{_fmtIST(b.sold_at)}</span>
      {b.released_at && <><span className="text-muted-foreground">Released</span><span className="text-right">{_fmtIST(b.released_at)}</span></>}
      <span className="text-muted-foreground">Status</span><span className="text-right uppercase">{row.status.replace(/_/g, " ")}</span>
    </div>
  );
}

function SalesView() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  useEffect(() => {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (status) qs.set("status", status);
    if (dfrom) qs.set("date_from", dfrom);
    if (dto) qs.set("date_to", dto);
    api.get(`/seller/sales-detail?${qs}`).then((r) => setRows(r.data)).catch(() => {});
  }, [q, status, dfrom, dto]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <input data-testid="sales-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search design or txn id…" className="border border-border rounded px-3 py-1.5 text-sm w-full md:w-64" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm">
          <option value="">Any status</option>
          <option value="pending">Pending</option>
          <option value="available">Available</option>
          <option value="on_hold_dispute">On hold (dispute)</option>
          <option value="withdrawn">Withdrawn</option>
          <option value="refunded">Refunded</option>
        </select>
        <input type="date" value={dfrom} onChange={(e) => setDfrom(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm" />
        <input type="date" value={dto} onChange={(e) => setDto(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm" />
        <span className="text-xs text-muted-foreground ml-auto">{rows.length} row{rows.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left"><tr>{["Sold", "Design", "Txn ID", "Buyer paid", "Commission", "Gateway", "TDS", "Net", "Release", "Status"].map((h) => <th key={h} className="px-3 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border">
                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{_fmtIST(r.sold_at)}</td>
                <td className="px-3 py-2 font-medium">{r.listing_title}</td>
                <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">{r.id?.slice(0, 10)}…</td>
                <td className="px-3 py-2 font-mono">{inr(r.gross)}</td>
                <td className="px-3 py-2 font-mono text-red-600">−{inr(r.commission)}</td>
                <td className="px-3 py-2 font-mono text-red-600">−{inr(r.gateway)}</td>
                <td className="px-3 py-2 font-mono text-red-600">−{inr(r.tds)}</td>
                <td className="px-3 py-2 font-mono font-semibold">{inr(r.net)}</td>
                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.release_at ? _fmtIST(r.release_at) : "—"}</td>
                <td className="px-3 py-2"><StatusTag status={r.status} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-16 text-center text-muted-foreground">No sales in this window.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayoutsView() {
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(null);
  useEffect(() => { api.get("/seller/payouts-detail").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-2">
      {rows.map((p) => (
        <div key={p.id} data-testid={`payout-row-${p.id}`} className="border border-border rounded-md bg-card p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-lg font-mono">{inr(p.amount)}</span>
                <StatusTag status={p.status} />
                <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{p.method}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">Requested {_fmtIST(p.created_at)}{p.paid_at ? ` · Paid ${_fmtIST(p.paid_at)}` : ""}</div>
              <div className="text-xs text-muted-foreground mt-0.5">To <span className="font-mono">{p.destination_masked}</span></div>
            </div>
            {p.status === "paid" && p.utr_reference && (
              <div className="text-right">
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Bank UTR</p>
                <div className="flex items-center gap-1.5">
                  <span data-testid={`payout-utr-${p.id}`} className="font-mono text-sm font-semibold select-all">{p.utr_reference}</span>
                  <button onClick={() => _copy(p.utr_reference)} className="text-xs text-primary underline">copy</button>
                </div>
              </div>
            )}
          </div>
          {p.covered_sales && p.covered_sales.length > 0 && (
            <button onClick={() => setExpanded(expanded === p.id ? null : p.id)} className="mt-2 text-xs text-primary hover:underline inline-flex items-center gap-1">
              {expanded === p.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {p.covered_sales.length} sales covered
            </button>
          )}
          {expanded === p.id && p.covered_sales && (
            <div className="mt-2 pt-2 border-t border-border grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
              {p.covered_sales.map((s) => (
                <div key={s.id} className="flex justify-between font-mono">
                  <span>{s.listing_title}</span>
                  <span className="font-semibold">{inr(s.seller_earning)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {rows.length === 0 && <div className="border border-dashed border-border rounded-md p-16 text-center text-muted-foreground">No payouts yet.</div>}
    </div>
  );
}


function UploadForm({ cats, onDone }) {
  const { user } = useAuth();
  const hideEncourage = !!user?.prefs?.hide_encourage_own_render;
  const [showEncourage, setShowEncourage] = useState(false);
  const [f, setF] = useState({ title: "", description: "", category_id: "", price: "", tags: "", size_value: "", cam_weight: "", stone_details: "" });
  const [sizes, setSizes] = useState({ ring_sizes: [], bangle_sizes: [] });
  useEffect(() => { api.get("/config/sizes").then((r) => setSizes(r.data)).catch(() => {}); }, []);
  const [stlFile, setStlFile] = useState(null);
  const [dmFile, setDmFile] = useState(null);
  // Server-configured max upload size (MB) — fetched once on mount so
  // we can fail fast client-side. Falls back to 200 MB if the public
  // settings endpoint isn't reachable.
  const [maxUploadMb, setMaxUploadMb] = useState(200);
  useEffect(() => {
    api.get("/settings/public").then((r) => {
      const v = r.data?.max_stl_upload_mb;
      if (Number.isFinite(v) && v > 0) setMaxUploadMb(v);
    }).catch(() => {});
  }, []);
  const [loading, setLoading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);           // real byte-level %
  const [uploadPhase, setUploadPhase] = useState("");      // Uploading → Processing → Live
  const [photosReady, setPhotosReady] = useState(0);       // 0..8 as renders land
  const EXPECTED_PHOTOS = 8;
  const [renderErr, setRenderErr] = useState("");          // set when subprocess reports failure
  const [lastListingId, setLastListingId] = useState(""); // for the Retry button
  const [retrying, setRetrying] = useState(false);
  // Live server-side render job — populated as soon as the upload lands
  // and the studio pipeline starts emitting progress events. Drives the
  // RenderProgressPanel tile grid inside the upload panel.
  // 2026-02-28 — client-side render pipeline replaces server-side.
  // Once /listings returns the new listing id, we render the 8 studio
  // views in the seller's browser via <ClientStudioRenderer>. State
  // shape: { id, title, stlUrl, dmUrl } | null.
  const [pendingClientRender, setPendingClientRender] = useState(null);
  const [renderJob, setRenderJob] = useState(null);
  const [cancellingUpload, setCancellingUpload] = useState(false);
  const cancelUploadRender = async () => {
    if (!lastListingId || cancellingUpload) return;
    if (!window.confirm("Stop this render? The slot will free up for the next upload.")) return;
    setCancellingUpload(true);
    try {
      const { data } = await api.post(`/seller/listings/${lastListingId}/photos/cancel`);
      if (data.cancelled) {
        toast.success("Render cancelled — the slot is free.");
      } else {
        toast.info("No render was running.");
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setCancellingUpload(false);
    }
  };
  const [aiLoading, setAiLoading] = useState(false);
  const [confirmIP, setConfirmIP] = useState(false);
  const w = computeWeights(f.cam_weight);
  // Detect if the currently-picked category is a ring / bangle so we show
  // the right size dropdown — using exact matches on the category name so
  // "Earrings" doesn't accidentally trip the "ring" branch.
  const catName = (cats.find((c) => c.id === f.category_id)?.name || "").toLowerCase().trim();
  const isRing = catName === "rings" || catName === "ring";
  const isBangle = catName.startsWith("bangle");
  const sizeOptions = isRing ? sizes.ring_sizes : isBangle ? sizes.bangle_sizes : [];
  const genAI = async () => {
    if (!f.title || !f.category_id) return toast.error("Add a title and category first");
    setAiLoading(true);
    try {
      const cat = cats.find((c) => c.id === f.category_id);
      const { data } = await api.post("/ai/describe", {
        title: f.title, category_name: cat?.name,
        tags: f.tags ? f.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
        ring_size: f.size_value, cam_weight: f.cam_weight ? Number(f.cam_weight) : null,
        stone_details: f.stone_details, file_type: dmFile ? "3dm" : "stl", editable: !!dmFile,
      });
      const extra = (data.hashtags || []).map((h) => h.replace(/^#/, "")).join(", ");
      setF((s) => ({ ...s, description: data.description, tags: s.tags ? `${s.tags}, ${extra}` : extra }));
      toast.success("AI description generated");
    } catch (e) { toast.error(apiErr(e)); } finally { setAiLoading(false); }
  };

  // Poll the render-progress endpoint until the job settles (complete
  // or failed) or the 4-min ceiling trips. Feeds live per-view state
  // into `renderJob` so the upload panel's RenderProgressPanel updates
  // its tile grid in real time.
  const pollPhotos = async (listingId) => {
    const MAX_MS = 4 * 60 * 1000;
    const startedAt = Date.now();
    let landed = 0;
    while (Date.now() - startedAt < MAX_MS) {
      try {
        const [prog, phRes] = await Promise.all([
          api.get(`/seller/listings/${listingId}/render-progress`),
          api.get(`/seller/listings/${listingId}/photos`),
        ]);
        const job = prog.data?.job || null;
        setRenderJob(job);
        landed = (phRes.data.photos || []).length;
        setPhotosReady(landed);
        // Prefer the server-computed percent (per-view) over the coarse
        // count-based fallback so the bar advances at 12.5% per landing.
        setUploadPct(job?.percent ?? Math.min(100, Math.round((landed / EXPECTED_PHOTOS) * 100)));
        if (job && job.status === "complete") {
          return { status: "complete", landed };
        }
        if (job && job.status === "failed") {
          return { status: "error", landed, error: job.error || phRes.data.render_error || "Rendering failed." };
        }
        // Legacy fallback: some paths (retry from an old client) may set
        // render_error on the listing without producing a job doc.
        if (phRes.data.render_error && phRes.data.processing === false) {
          return { status: "error", landed, error: phRes.data.render_error };
        }
      } catch { /* transient — keep polling */ }
      await new Promise((r) => setTimeout(r, 1000));
    }
    return { status: "timeout", landed };
  };

  const retryRender = async () => {
    if (!lastListingId || retrying) return;
    setRetrying(true);
    setRenderErr("");
    setPhotosReady(0);
    setUploadPct(0);
    setUploadPhase("Generating studio photos");
    setLoading(true);
    try {
      await api.post(`/seller/listings/${lastListingId}/photos/regenerate`);
      const result = await pollPhotos(lastListingId);
      if (result.status === "complete") {
        toast.success("Draft saved — upload your own render (optional) and pick a cover to publish.");
        onDone({ id: lastListingId, title: f.title });
      } else if (result.status === "error") {
        setRenderErr(result.error);
        toast.error(`Retry failed: ${result.error}`);
      } else {
        toast.info(`Retry still running · ${result.landed}/${EXPECTED_PHOTOS} landed so far.`);
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setRetrying(false);
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!stlFile) return toast.error("An STL file is required. Please upload a .stl to continue — 3DM alone can't power the renderer or the browser preview.");
    if (!confirmIP) return toast.error("Please confirm you own the rights to this design before uploading.");
    // 2026-02 upload ceiling — fail fast on the client so sellers don't
    // wait 3 min to upload a 500 MB STL only to see a server-side 413.
    // We fetch the ceiling on component mount (see `maxUploadMb` state)
    // and default to a conservative 200 MB when the setting is missing.
    const limit = maxUploadMb || 200;
    const overSize = (stlFile && stlFile.size > limit * 1024 * 1024)
                  || (dmFile && dmFile.size > limit * 1024 * 1024);
    if (overSize) {
      const which = stlFile.size > limit * 1024 * 1024 ? "STL" : "3DM";
      const mb = ((which === "STL" ? stlFile : dmFile).size / (1024 * 1024)).toFixed(1);
      return toast.error(
        `This ${which} is ${mb} MB — larger than our ${limit} MB rendering limit. ` +
        `Please decimate or export a smaller version, or contact support for help with heavy files.`
      );
    }
    setLoading(true);

    /* ---- R2 direct-upload path ----
     * For files larger than ~90MB we upload direct to Cloudflare R2 to
     * bypass the platform edge's 100MB request cap. Smaller files fall
     * back to the classic multipart POST so nothing regresses for the
     * existing seller workflow. Both paths are supported server-side. */
    const R2_THRESHOLD_BYTES = 90 * 1024 * 1024;
    const useR2 = (stlFile && stlFile.size >= R2_THRESHOLD_BYTES) ||
                  (dmFile && dmFile.size >= R2_THRESHOLD_BYTES);
    const r2Keys = {};
    if (useR2) {
      try {
        const uploadOne = async (file, ext, label) => {
          if (!file) return null;
          setUploadPhase(`Preparing ${label}`);
          setUploadPct(0);
          const { data: pres } = await api.post("/uploads/r2/presign", {
            ext,
            content_length: file.size,
          });
          setUploadPhase(`Uploading ${label} to storage`);
          // Native XHR — axios can't stream request bodies to a URL that
          // isn't the API base and we need a real progress event.
          await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("PUT", pres.upload_url);
            xhr.setRequestHeader("Content-Type", pres.content_type);
            xhr.upload.onprogress = (evt) => {
              if (evt.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100));
            };
            xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
              ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
            xhr.onerror = () => reject(new Error("Upload network error"));
            xhr.send(file);
          });
          await api.post("/uploads/r2/confirm", { object_key: pres.object_key });
          return pres.object_key;
        };
        r2Keys.stl_object_key = await uploadOne(stlFile, "stl", "STL");
        r2Keys.dm_object_key  = await uploadOne(dmFile, "3dm", "3DM");
      } catch (err) {
        setLoading(false);
        setUploadPct(0);
        setUploadPhase("");
        return toast.error(err?.response?.data?.detail || err?.message || "Direct upload failed.");
      }
    }

    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => v && fd.append(k, v));
    // When we used R2, send only the object_keys; the file bytes are
    // already in storage. When we used the classic path, attach the files.
    if (useR2) {
      if (r2Keys.stl_object_key) fd.append("stl_object_key", r2Keys.stl_object_key);
      if (r2Keys.dm_object_key) fd.append("dm_object_key", r2Keys.dm_object_key);
    } else {
      if (stlFile) fd.append("stl_file", stlFile);
      if (dmFile) fd.append("dm_file", dmFile);
    }
    // Client-side WebGL previews are no longer generated or uploaded —
    // `render_stl_studio` on the backend is the ONE renderer that produces
    // marketplace photos. This keeps every listing visually consistent
    // (same material, same background, same lighting) regardless of the
    // seller's browser or GPU, and matches the admin Render Presets panel.
    if (!useR2) {
      setUploadPct(0);
      setUploadPhase("Uploading");
    } else {
      setUploadPhase("Finalising");
    }
    try {
      const { data } = await api.post("/listings", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 5 * 60 * 1000,
        onUploadProgress: (evt) => {
          if (!useR2 && evt.total) {
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setUploadPct(pct);
            if (pct >= 100) setUploadPhase("Uploaded — waiting for renders");
          }
        },
      });
      logTermsCheckpoint({
        role: "seller",
        event: "upload",
        contextId: data?.id,
        checkboxes: ["seller_terms", "seller_ip_warranty"],
      });

      // -------- Client-side studio render kicks in from here ----------
      // Server-side rendering was removed on 2026-02-28. The upload
      // response includes the STL/3DM URLs so ClientStudioRenderer can
      // load them straight from the seller's session and render the 8
      // studio views entirely in-browser. onComplete → open PhotoManager
      // for cover selection; onFailure → open PhotoManager which shows
      // the manual-upload path.
      setLastListingId(data.id);
      if (!hideEncourage) setShowEncourage(true);
      // 2026-02-28 — the browser renderer fetches raw bytes via
      // `GET /api/seller/listings/{id}/render-source?kind=stl` (auth'd
      // through the api client). We pass a boolean saying which format
      // is available; ClientStudioRenderer will resolve the URL itself.
      setPendingClientRender({
        id: data.id,
        title: f.title,
        hasStl: !!data.stl_file,
        hasDm:  !!data.dm_file,
        rawData: data,
      });
      // Stop the outer spinner — the ClientStudioRenderer panel below
      // takes over the "in progress" UI now.
      setLoading(false);
      setUploadPct(0);
      setUploadPhase("");
      return;
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setLoading(false);
      setUploadPct(0);
      setUploadPhase("");
      setPhotosReady(0);
    }
  };
  // eslint-disable-next-line no-unused-vars
  const _unused_polling_flow_kept_for_regenerate = async () => {
      // -------- Poll the photos endpoint until the 8 studio views land --
      // The listing doc comes back immediately from POST /listings with
      // `processing: true` and an empty photos array — server-side
      // `render_stl_studio` runs in the background and takes ~20s on a
      // typical 500k-triangle STL. We poll every 3s, updating the count
      // shown in the progress bar so the seller sees real progress, not
      // a spinner. Cap at 4 minutes so a stuck render doesn't hang the UI.
      setUploadPhase("Generating studio photos");
      setUploadPct(0);
      setPhotosReady(0);
      setRenderErr("");
      setLastListingId("");
      // Nudge the seller to upload their OWN render images while the
      // auto studio photos generate in the background. Shown once per
      // listing; the "Don't show this again" checkbox inside the modal
      // sets `hide_encourage_own_render` on the user profile.
      if (!hideEncourage) setShowEncourage(true);
      // Legacy polling removed — the client renderer above now drives progress.
      const pollResult = { status: "complete", landed: 0 };
      if (pollResult.status === "complete") {
        toast.success("Design submitted — draft saved. Upload your own render (optional) and pick a cover to publish.");
      } else if (pollResult.status === "error") {
        // Friendly toast when the renderer can't handle this STL — the
        // panel below already shows the "Upload your own photos" CTA.
        if (/upload your own render/i.test(pollResult.error)) {
          toast.error("Studio photos couldn't be generated. Please upload your own render images.");
        } else {
          toast.error(`Rendering failed: ${pollResult.error}`);
        }
        setRenderErr(pollResult.error);
        setLoading(false);
        return;
      } else if (pollResult.landed > 0) {
        toast.warning(`Design submitted. ${pollResult.landed}/${EXPECTED_PHOTOS} photos ready — the remaining renders will finish in the background.`);
      } else {
        toast.info("Design submitted — studio photos are still generating. Check the Listings tab in ~30 seconds.");
      }
  };
  return (
    <form onSubmit={submit} className="max-w-2xl border border-border rounded-md bg-card p-6 space-y-4">
      {/* 2026-02-28 — Client-side studio-render panel appears once the
          upload lands. It renders + uploads all 8 studio views entirely
          in the seller's browser via three.js. On success → open the
          PhotoManager for cover selection. On failure → still open the
          PhotoManager so the seller can use the manual-upload path. */}
      {pendingClientRender && (
        <ClientStudioRenderer
          key={pendingClientRender.id}
          listingId={pendingClientRender.id}
          hasStl={pendingClientRender.hasStl}
          hasDm={pendingClientRender.hasDm}
          onComplete={(count) => {
            toast.success(`Rendered ${count} studio views in your browser — pick a cover to publish.`);
            const done = pendingClientRender;
            setPendingClientRender(null);
            onDone({ id: done.id, title: done.title });
          }}
          onFailure={(reason) => {
            // eslint-disable-next-line no-console
            console.warn("[ClientStudioRenderer] failed:", reason);
            toast.warning(
              "Your browser couldn't generate studio renders. Please upload your own photos to continue.",
            );
            const done = pendingClientRender;
            setPendingClientRender(null);
            // Hand off to PhotoManager — it already has the manual-
            // upload UI + publish gate for the empty-photos state.
            onDone({ id: done.id, title: done.title });
          }}
        />
      )}
      <h2 className="font-display font-semibold text-lg">Upload a new design</h2>
      <p className="text-xs text-muted-foreground -mt-2">Upload the print-ready <b>STL</b> and/or the editable <b>3DM</b>. The STL powers the fast live preview; buyers see which formats are available.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="border border-dashed border-border rounded-md p-3">
          <label className="text-sm font-medium">STL file (print-ready)</label>
          <input type="file" accept=".stl" data-testid="upload-stl" onChange={(e) => { const file = e.target.files[0]; setStlFile(file); }} className="mt-1 w-full text-xs bg-background" />
          {stlFile && <p className="font-mono text-[11px] mt-1 text-slate-500">{stlFile.name}</p>}
        </div>
        <div className="border border-dashed border-border rounded-md p-3">
          <label className="text-sm font-medium">3DM file (editable)</label>
          <input type="file" accept=".3dm" data-testid="upload-3dm" onChange={(e) => setDmFile(e.target.files[0])} className="mt-1 w-full text-xs bg-background" />
          {dmFile && <p className="font-mono text-[11px] mt-1 text-emerald-600">{dmFile.name}</p>}
        </div>
      </div>
      {stlFile && (
        <div className="border border-dashed border-primary/30 bg-primary/5 rounded-md p-3 text-xs text-muted-foreground">
          <b className="text-primary">STL selected · {stlFile.name}</b>
          <br />
          Your 8 studio photos will be generated <b>in your browser</b> as soon as the upload lands — matte grey on studio black with ambient occlusion, matching every listing on the marketplace. Nothing to wait for on our side. If your browser can&apos;t render this file we&apos;ll switch you to the manual photo-upload flow automatically.
        </div>
      )}
      <Input label="Title" value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="upload-title" />
      <div>
        <label className="text-sm font-medium">Category</label>
        <select required value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })} data-testid="upload-category" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background">
          <option value="">Select…</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">Description</label>
          <button type="button" onClick={genAI} disabled={aiLoading} data-testid="ai-describe-btn" className="flex items-center gap-1.5 text-xs font-medium rounded-md px-2.5 py-1 border transition-colors disabled:opacity-60" style={{ borderColor: "hsl(var(--gold) / 0.5)", color: "hsl(var(--gold))" }}>
            {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Generate with AI
          </button>
        </div>
        <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={4} data-testid="upload-description" className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input label="Price (INR)" type="number" value={f.price} onChange={(v) => setF({ ...f, price: v })} testid="upload-price" />
        {(isRing || isBangle) ? (
          <label className="block" data-testid="size-dropdown-wrap">
            <span className="text-sm font-medium">{isRing ? "Ring size" : "Bangle size"} (Indian standard)</span>
            <select
              value={f.size_value}
              onChange={(e) => setF({ ...f, size_value: e.target.value })}
              data-testid="upload-size-value"
              className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background"
            >
              <option value="">— Choose size —</option>
              {sizeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        ) : null}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input label="CAM / wax weight (g)" type="number" value={f.cam_weight} onChange={(v) => setF({ ...f, cam_weight: v })} testid="upload-cam" />
      </div>
      {w.gold_18k && (
        <div className="border rounded-md p-3" style={{ borderColor: "hsl(var(--gold) / 0.4)", background: "hsl(var(--gold) / 0.05)" }}>
          <p className="text-[11px] uppercase tracking-widest font-semibold mb-2" style={{ color: "hsl(var(--gold))" }}>Auto-estimated metal weight (shown to buyers)</p>
          <div className="grid grid-cols-4 gap-2 font-mono text-xs text-center">
            <div>22K<br /><b>{w.gold_22k} g</b></div><div>18K<br /><b>{w.gold_18k} g</b></div><div>14K<br /><b>{w.gold_14k} g</b></div><div>Silver<br /><b>{w.silver} g</b></div>
          </div>
        </div>
      )}
      <Input label="Stone details (optional)" value={f.stone_details} onChange={(v) => setF({ ...f, stone_details: v })} />
      <Input label="Tags (comma separated)" value={f.tags} onChange={(v) => setF({ ...f, tags: v })} />
      <label className="flex items-start gap-2 text-xs border border-amber-500/40 bg-amber-500/10 rounded-md p-3" data-testid="upload-ip-confirm">
        <input
          type="checkbox"
          checked={confirmIP}
          onChange={(e) => setConfirmIP(e.target.checked)}
          className="mt-0.5"
          data-testid="upload-ip-confirm-input"
        />
        <span className="leading-snug">
          <b>Ownership warranty</b> — I confirm this design is my original work and I own all rights to distribute it on Jeweltix. Uploading stolen or infringing designs may result in permanent account termination and legal action.
        </span>
      </label>
      {loading && (
        <div className="border border-primary/30 bg-primary/5 rounded-md p-4" data-testid="upload-progress-block">
          <div className="flex items-center gap-4">
            {uploadPhase === "Generating studio photos" && (
              <img
                src={`${process.env.PUBLIC_URL || ""}/render-loop.webp`}
                alt="Rendering preview"
                width={96}
                height={96}
                className="rounded-md border border-primary/20 flex-shrink-0"
                data-testid="upload-render-loop"
                style={{ background: "#B8D8F0" }}
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between text-xs font-mono uppercase tracking-wider text-primary mb-2">
                <span>{uploadPhase || "Uploading"}</span>
                <span data-testid="upload-progress-pct">{uploadPct}%</span>
              </div>
              <div className="h-2 rounded-full bg-primary/15 overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${uploadPct}%` }}
                  data-testid="upload-progress-bar"
                />
              </div>
              {renderJob && (renderJob.status === "queued" || renderJob.status === "running") ? (
                <div className="mt-3" data-testid="upload-render-progress-wrap">
                  <RenderProgressPanel
                    job={renderJob}
                    imgBase={process.env.REACT_APP_BACKEND_URL || ""}
                    compact
                    onCancel={cancelUploadRender}
                    cancelling={cancellingUpload}
                  />
                </div>
              ) : renderErr ? (() => {
                const needsManual = /upload your own render/i.test(renderErr);
                return (
                  <div
                    className="mt-3 flex items-start justify-between gap-3"
                    data-testid="upload-render-error-row"
                  >
                    <p className="text-[12px] text-destructive font-medium leading-snug">
                      {needsManual ? renderErr : `Rendering failed: ${renderErr}`}
                    </p>
                    {needsManual ? (
                      <button
                        type="button"
                        onClick={() => {
                          // Bounce the seller into Manage Photos where they
                          // can upload their own render images — the only
                          // fix path when the studio renderer can't
                          // handle this STL.
                          onDone({ id: lastListingId });
                        }}
                        data-testid="upload-manual-photos-btn"
                        className="inline-flex items-center gap-1.5 border-2 border-primary text-primary rounded-md px-3 py-1.5 text-[12px] font-bold hover:bg-primary/5 flex-shrink-0"
                      >
                        Upload your own photos
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={retryRender}
                        disabled={retrying}
                        data-testid="upload-render-retry-btn"
                        className="inline-flex items-center gap-1.5 border-2 border-destructive/60 text-destructive rounded-md px-3 py-1.5 text-[12px] font-bold hover:bg-destructive/5 disabled:opacity-60 flex-shrink-0"
                      >
                        {retrying ? "Retrying…" : "Try again"}
                      </button>
                    )}
                  </div>
                );
              })() : uploadPhase === "Generating studio photos" ? (
                <p className="text-[11px] text-muted-foreground mt-2" data-testid="photos-ready-count">
                  {photosReady} of {EXPECTED_PHOTOS} studio views ready · matte grey on studio blue · takes about 20 seconds
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground mt-2">You can switch tabs — the upload will keep running.</p>
              )}
            </div>
          </div>
        </div>
      )}
      <button disabled={loading || !confirmIP || !stlFile} data-testid="submit-listing" className="flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-5 py-2.5 font-semibold hover:bg-primary/90 disabled:opacity-50">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Submit for review</button>
      {!stlFile && (
        <p className="text-[11px] text-amber-600 font-medium -mt-1" data-testid="stl-required-hint">
          STL file is required to submit — the renderer and 3D preview both need STL geometry. Attach a .3dm alongside if you have one, but it can't replace the STL.
        </p>
      )}

      {showEncourage && lastListingId && (
        <EncourageOwnRenderModal
          listingId={lastListingId}
          onClose={() => setShowEncourage(false)}
          onUploaded={() => setShowEncourage(false)}
        />
      )}
    </form>
  );
}

function BulkUploadForm({ cats, onDone, onOpenPhotos }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aiAllLoading, setAiAllLoading] = useState(false);
  const [overallPct, setOverallPct] = useState(0);
  const [phase, setPhase] = useState("");                     // Uploading / Processing / Done / Review
  const [limits, setLimits] = useState({ batch_cap: 10, hourly_cap: 40, max_file_mb: 60 });
  const [processingIdx, setProcessingIdx] = useState(-1);
  // 2026-02 Phase 2 — post-batch review state
  const [review, setReview] = useState({});
  const [reviewLoading, setReviewLoading] = useState(false);
  // 2026-02-28 — Bulk client-render queue. After the batch upload
  // completes, every successful row enters `browserRenderQueue` and the
  // outer effect below pops one at a time into `activeBrowserRender`,
  // which mounts a <ClientStudioRenderer> at the top of the panel. The
  // renderer's onComplete/onFailure advances the queue. Sequential
  // (not parallel) so we never spike the seller's GPU with 8 concurrent
  // three.js scenes.
  const [browserRenderQueue, setBrowserRenderQueue] = useState([]);
  const [activeBrowserRender, setActiveBrowserRender] = useState(null);
  const cancelRef = useRef(false);

  // Fetch admin-configured caps so we can (a) display them before file
  // selection and (b) trim/reject over-limit selections client-side.
  useEffect(() => {
    api.get("/config/upload-limits").then((r) => setLimits(r.data)).catch(() => {});
  }, []);

  const onFiles = (fileList) => {
    const incoming = Array.from(fileList).filter((x) => /\.(stl|3dm)$/i.test(x.name));
    // Files ≥90MB take the R2 direct-upload path (see uploadSingle) which
    // bypasses the platform edge cap entirely. So the client-side ceiling
    // is R2's own 500MB per-file limit, not the admin's `max_file_mb`
    // multipart-only cap.
    const R2_MAX_BYTES = 500 * 1024 * 1024;
    // Server render-pipeline ceiling — anything above `max_stl_upload_mb`
    // (default 200 MB) is rejected by /api/upload with HTTP 413 anyway,
    // so surface a clear message BEFORE bytes go over the wire. Falls back
    // to R2_MAX_BYTES if the settings fetch failed for any reason.
    const RENDER_MAX_BYTES = (limits.max_stl_upload_mb || 200) * 1024 * 1024;
    let oversize = 0;
    let overRender = 0;
    let overCap = 0;
    const validated = [];
    for (const file of incoming) {
      if (file.size > R2_MAX_BYTES) { oversize += 1; continue; }
      if (file.size > RENDER_MAX_BYTES) { overRender += 1; continue; }
      validated.push(file);
    }
    setRows((prev) => {
      const combined = [...prev, ...validated.map((file) => ({
        // Every row gets a stable unique id so React `key` doesn't collide
        // when the seller picks two files with identical names from
        // different folders (rare but real — e.g. bulk re-uploads).
        _key: (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        file, filename: file.name, size: file.size,
        title: file.name.replace(/\.(stl|3dm)$/i, ""),
        category_id: "", price: "", ring_size: "", cam_weight: "",
        description: "", tags: "", aiLoading: false,
        status: "queued", listingId: null, error: null,
        pct: 0, stage: "Queued", attempts: 0,
      }))];
      // Trim to the admin batch cap
      if (combined.length > limits.batch_cap) {
        overCap = combined.length - limits.batch_cap;
      }
      const trimmed = combined.slice(0, limits.batch_cap);
      if (overCap > 0) {
        toast.warning(`You selected ${combined.length} designs. The limit is ${limits.batch_cap} per batch — the first ${limits.batch_cap} were queued. Please upload the rest in a second batch.`);
      }
      return trimmed;
    });
    if (oversize > 0) {
      toast.error(`${oversize} file${oversize > 1 ? "s were" : " was"} rejected — max file size is 500 MB.`);
    }
    if (overRender > 0) {
      const mb = limits.max_stl_upload_mb || 200;
      toast.error(`${overRender} file${overRender > 1 ? "s were" : " was"} larger than our ${mb} MB rendering limit — please decimate or export a smaller version before uploading.`);
    }
  };
  const update = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const remove = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const genRow = async (i) => {
    const row = rows[i];
    if (!row.title || !row.category_id) { toast.error("Add a title and category for this row first"); return false; }
    update(i, "aiLoading", true);
    try {
      const cat = cats.find((c) => c.id === row.category_id);
      const is3dm = /\.3dm$/i.test(row.filename);
      const { data } = await api.post("/ai/describe", {
        title: row.title, category_name: cat?.name,
        tags: row.tags ? row.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
        ring_size: row.ring_size, cam_weight: row.cam_weight ? Number(row.cam_weight) : null,
        file_type: is3dm ? "3dm" : "stl", editable: is3dm,
      });
      const extra = (data.hashtags || []).map((h) => h.replace(/^#/, "")).join(", ");
      setRows((r) => r.map((rw, idx) => (idx === i ? { ...rw, description: data.description, tags: rw.tags ? `${rw.tags}, ${extra}` : extra, aiLoading: false } : rw)));
      return true;
    } catch (e) { update(i, "aiLoading", false); toast.error(apiErr(e)); return false; }
  };
  const genAll = async () => {
    const targets = rows.map((r, i) => ({ r, i })).filter(({ r }) => r.title && r.category_id && !r.description);
    if (targets.length === 0) return toast.info("Add a title + category to rows first (already-generated rows are skipped)");
    setAiAllLoading(true);
    let ok = 0;
    for (const { i } of targets) {
      // eslint-disable-next-line no-await-in-loop
      if (await genRow(i)) ok += 1;
    }
    setAiAllLoading(false);
    if (ok) toast.success(`AI copy generated for ${ok} design${ok > 1 ? "s" : ""}`);
  };

  // -------------------------------------------------- SEQUENTIAL PROCESSOR
  //
  // POSTs one file at a time. Never issues a parallel upload. Each file
  // reports live XHR upload % and transitions through stages:
  //   Queued → Uploading → Processing (backend renders) → Complete / Failed
  // On transient network failures we auto-retry up to 3 times with
  // exponential backoff; permanent failures are marked "failed" with the
  // exact backend reason and the queue continues to the next file.
  // The seller can Cancel mid-batch — queued files are marked cancelled,
  // in-flight upload aborts, already-complete files remain.
  const uploadSingle = async (i, attempt = 0) => {
    const row = rows[i];
    const setRow = (patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    setRow({ status: "uploading", stage: "Uploading", pct: 0, attempts: attempt + 1, error: null });

    // Bulk upload no longer runs client-side WebGL rendering. Server
    // `render_stl_studio` is the sole source of marketplace photos —
    // consistent material, background and lighting across every listing.

    // ---- R2 direct-upload path for files ≥90MB ----
    // Bypasses the platform edge's 100MB request cap. Smaller files still
    // take the classic multipart route because the extra presign round-trip
    // isn't worth it for a 5MB ring STL.
    const R2_THRESHOLD_BYTES = 90 * 1024 * 1024;
    const isDm = /\.3dm$/i.test(row.filename);
    let r2ObjectKey = null;
    if (row.file.size >= R2_THRESHOLD_BYTES) {
      try {
        setRow({ stage: "Preparing large upload", pct: 5 });
        const { data: pres } = await api.post("/uploads/r2/presign", {
          ext: isDm ? "3dm" : "stl",
          content_length: row.file.size,
        });
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", pres.upload_url);
          xhr.setRequestHeader("Content-Type", pres.content_type);
          xhr.upload.onprogress = (evt) => {
            if (!evt.total) return;
            // Reserve 0-85 for R2 PUT, 85-90 for confirm, 90-100 for finalise.
            const pct = 5 + Math.round((evt.loaded / evt.total) * 80);
            setRow({ pct, stage: "Uploading to storage" });
          };
          xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
            ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
          xhr.onerror = () => reject(new Error("Upload network error"));
          xhr.send(row.file);
        });
        setRow({ pct: 88, stage: "Verifying" });
        await api.post("/uploads/r2/confirm", { object_key: pres.object_key });
        r2ObjectKey = pres.object_key;
        setRow({ pct: 90, stage: "Finalising" });
      } catch (r2err) {
        const reason = r2err?.response?.data?.detail || r2err?.message || "Direct upload failed";
        setRow({ status: "failed", stage: "Failed", pct: 0, error: reason });
        return false;
      }
    }

    const fd = new FormData();
    if (r2ObjectKey) {
      fd.append(isDm ? "dm_object_key" : "stl_object_key", r2ObjectKey);
    } else {
      fd.append(isDm ? "dm_file" : "stl_file", row.file, row.filename);
    }
    fd.append("title", row.title);
    fd.append("description", row.description || "");
    fd.append("category_id", row.category_id);
    fd.append("price", String(row.price));
    if (row.tags) fd.append("tags", row.tags);
    if (row.ring_size) fd.append("ring_size", row.ring_size);
    if (row.cam_weight) fd.append("cam_weight", row.cam_weight);
    // Bulk upload — client-side preview pixels are NOT persisted. Server
    // `render_stl_studio` produces the marketplace photos from the STL,
    // ensuring every listing renders with the same admin-configured
    // material/lighting/background. See PRD 2026-02-19 consolidation.

    try {
      const { data } = await api.post("/listings", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 8 * 60 * 1000,
        onUploadProgress: (evt) => {
          if (r2ObjectKey || !evt.total) return;   // R2 path already reports its own %
          const pct = Math.round((evt.loaded / evt.total) * 90);   // 0-90 for upload, 90-100 = processing
          setRow({ pct, stage: pct < 90 ? "Uploading" : "Processing" });
        },
      });
      setRow({
        status: data.processing ? "processing" : "live",
        stage: data.processing ? "Rendering in browser" : "Complete",
        pct: 100,
        listingId: data.id,
        error: null,
        // 2026-02-28 — stash the CAD urls so the client-render queue can
        // hand them straight to <ClientStudioRenderer>. Without these
        // the browser renderer would have to refetch the whole listing.
        stlUrl: data.stl_url || (data.stl_file ? `/seller/listings/${data.id}/render-source?kind=stl` : null),
        dmUrl:  data.dm_url  || (data.dm_file  ? `/seller/listings/${data.id}/render-source?kind=dm`  : null),
        hasStl: !!data.stl_file,
        hasDm:  !!data.dm_file,
      });
      return true;
    } catch (e) {
      // Auto-retry transient failures (network / 5xx) up to 3 attempts total.
      // For R2 uploads we DON'T retry because the presign has already been
      // consumed and the retry would need a fresh URL — safer to fail fast
      // and let the seller click Retry, which re-runs the full flow.
      const s = e.response?.status;
      const transient = !s || s >= 500 || e.code === "ECONNABORTED" || e.message?.includes("Network");
      if (!r2ObjectKey && transient && attempt < 2 && !cancelRef.current) {
        const wait = 1500 * Math.pow(2, attempt);       // 1.5s, 3s, 6s
        setRow({ status: "uploading", stage: `Retrying in ${wait / 1000}s (attempt ${attempt + 2}/3)`, pct: 0 });
        await new Promise((res) => setTimeout(res, wait));
        return uploadSingle(i, attempt + 1);
      }
      const reason = apiErr(e) || (transient ? "Network error — could not reach server" : "Upload failed");
      setRow({ status: "failed", stage: "Failed", pct: 0, error: reason });
      return false;
    }
  };

  const submitRows = async (targetIndexes = null) => {
    const indexes = targetIndexes ?? rows
      .map((_, i) => i)
      .filter((i) => rows[i].status === "queued" || rows[i].status === "failed");
    const valid = indexes.filter((i) => rows[i].title && rows[i].category_id && rows[i].price);
    if (valid.length === 0) return toast.error("Fill title, category & price for at least one file");
    const skipped = indexes.length - valid.length;
    if (skipped > 0) toast.warning(`${skipped} row${skipped > 1 ? "s were" : " was"} skipped — missing title, category, or price.`);

    setLoading(true);
    cancelRef.current = false;
    setPhase("Uploading");
    let ok = 0, fail = 0;
    for (let idx = 0; idx < valid.length; idx += 1) {
      if (cancelRef.current) {
        // Remaining queued rows get marked cancelled
        setRows((rs) => rs.map((r, i) => valid.slice(idx).includes(i) ? { ...r, status: "cancelled", stage: "Cancelled" } : r));
        break;
      }
      const i = valid[idx];
      setProcessingIdx(i);
      setOverallPct(Math.round((idx / valid.length) * 100));
      // eslint-disable-next-line no-await-in-loop
      const success = await uploadSingle(i);
      if (success) ok += 1; else fail += 1;
    }
    setProcessingIdx(-1);
    setOverallPct(100);
    setPhase("Done");
    setLoading(false);

    // Batch summary
    if (!cancelRef.current) {
      if (fail === 0) {
        toast.success(`${ok} design${ok > 1 ? "s" : ""} uploaded — rendering studio views in your browser now.`);
      } else {
        toast.warning(`${ok} uploaded successfully, ${fail} failed — see the "Failed uploads" section above to retry.`);
      }
      // 2026-02-28 — enqueue every successful row for browser rendering.
      // The queue-drain effect below pops one at a time; sequential so we
      // never spike the seller's GPU with concurrent three.js scenes.
      setRows((rs) => {
        const targets = rs
          .map((r, i) => ({ r, i }))
          .filter(({ r }) => r.listingId && (r.hasStl || r.hasDm))
          .map(({ r, i }) => ({
            listingId: r.listingId, title: r.title,
            hasStl: r.hasStl, hasDm: r.hasDm, rowIndex: i,
          }));
        if (targets.length > 0) {
          setBrowserRenderQueue((q) => [...q, ...targets]);
          setPhase("Rendering in browser");
        } else {
          pollReview();
        }
        return rs;
      });
    }
  };

  // 2026-02-28 — Browser-render queue drain effect.
  // Pops one job at a time into `activeBrowserRender`; the JSX below
  // mounts a <ClientStudioRenderer> for it; its onComplete/onFailure
  // clears the active slot and this effect fires again.
  useEffect(() => {
    if (activeBrowserRender) return;
    if (browserRenderQueue.length === 0) {
      // Everything drained — refresh the review panel once. Sellers see
      // per-row Publish / Fix photos buttons appear here.
      if (Object.keys(review).length === 0) pollReview();
      return;
    }
    const [next, ...rest] = browserRenderQueue;
    setActiveBrowserRender(next);
    setBrowserRenderQueue(rest);
    // Update per-row stage so the table shows "Rendering in browser".
    setRows((rs) => rs.map((r, i) => (i === next.rowIndex
      ? { ...r, stage: "Rendering in browser", status: "processing" }
      : r)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrowserRender, browserRenderQueue]);

  const finishActiveBrowserRender = (success) => {
    const done = activeBrowserRender;
    if (!done) return;
    setActiveBrowserRender(null);
    setRows((rs) => rs.map((r, i) => (i === done.rowIndex
      ? { ...r, stage: success ? "Rendered" : "Render failed — upload own", status: "live" }
      : r)));
    // Refresh review state so the summary card updates as each row lands.
    pollReview();
  };

  // Fetch publish-readiness for every row that landed successfully. Runs
  // after the batch completes and once again after a manual regenerate.
  // We poll once with a short delay because renders may still be in
  // flight — but the caller can trigger `pollReview()` again once they
  // return from PhotoManager if they want fresh state.
  const pollReview = async () => {
    const targets = rows.filter((r) => r.listingId && (r.status === "live" || r.status === "processing"));
    if (targets.length === 0) return;
    setReviewLoading(true);
    setPhase("Review");
    const next = {};
    await Promise.all(targets.map(async (r) => {
      try {
        const { data } = await api.get(`/seller/listings/${r.listingId}/photos`);
        const photos = data.photos || [];
        next[r.listingId] = {
          can_publish: !!data.can_publish,
          blockers: data.publish_blockers || [],
          status: data.status || "pending",
          manual_count: data.manual_count || 0,
          auto_count: photos.filter((p) => p.source === "auto").length,
          photos: photos.length,
        };
      } catch {
        next[r.listingId] = { can_publish: false, blockers: ["Could not check status — try Fix photos."], status: "unknown", manual_count: 0, auto_count: 0, photos: 0 };
      }
    }));
    setReview(next);
    setReviewLoading(false);
  };

  // Rewire retry to the CLIENT-side renderer. Instead of POSTing to the
  // (now legacy) server /photos/regenerate endpoint, we re-enqueue the
  // row into the browser render queue so it runs the same three.js
  // pipeline the initial batch used.
  const [retryingRenders, setRetryingRenders] = useState(false);
  const failedRenderRows = () =>
    Object.entries(review).filter(([, v]) => (v.auto_count + (v.client_render_count || 0)) === 0
                                              && v.status !== "approved");
  const retryOneRender = async (lid, title) => {
    // Find the row whose listingId matches; we need to know which formats are available.
    const row = rows.find((r) => r.listingId === lid);
    if (!row || (!row.hasStl && !row.hasDm)) {
      // Fallback — fetch the listing to learn which formats it has
      // (covers the page-reload case where row state was lost).
      try {
        const { data } = await api.get(`/listings/${lid}`);
        const hasStl = !!data.stl_file;
        const hasDm  = !!data.dm_file;
        if (!hasStl && !hasDm) return toast.error("The CAD file for this listing is not available.");
        setBrowserRenderQueue((q) => [...q, {
          listingId: lid, title: title || data.title, hasStl, hasDm,
          rowIndex: rows.findIndex((r) => r.listingId === lid),
        }]);
      } catch (e) { return toast.error(apiErr(e)); }
    } else {
      setBrowserRenderQueue((q) => [...q, {
        listingId: lid, title: title || row.title, hasStl: row.hasStl, hasDm: row.hasDm,
        rowIndex: rows.indexOf(row),
      }]);
    }
    toast.success(`Re-rendering "${title || 'design'}" in your browser — this takes a few seconds.`);
    setReview((rv) => ({ ...rv, [lid]: { ...rv[lid], blockers: ["Rendering in your browser…"], _retrying: true } }));
  };
  const retryAllFailedRenders = async () => {
    const failed = failedRenderRows();
    if (failed.length === 0) return toast.info("No failed renders to retry.");
    setRetryingRenders(true);
    for (const [lid] of failed) {
      // eslint-disable-next-line no-await-in-loop
      await retryOneRender(lid, null);
    }
    setRetryingRenders(false);
    toast.success(`Requeued ${failed.length} browser render${failed.length > 1 ? "s" : ""}.`);
  };

  // Publish a single row via the same server-side gate the PhotoManager uses.
  const publishOne = async (row) => {
    try {
      await api.post(`/seller/listings/${row.listingId}/publish`);
      toast.success(`"${row.title}" is now live on the marketplace.`);
      setReview((rv) => ({ ...rv, [row.listingId]: { ...rv[row.listingId], status: "approved", can_publish: false, blockers: [] } }));
    } catch (e) { toast.error(apiErr(e)); }
  };
  // Publish everything in the batch that's `can_publish=true`. Skips
  // already-live rows and rows still blocked.
  const publishAllReady = async () => {
    const ready = Object.entries(review).filter(([, v]) => v.can_publish);
    if (ready.length === 0) return toast.info("Nothing publish-ready yet — fix photos on the amber rows first.");
    if (!window.confirm(`Publish ${ready.length} design${ready.length > 1 ? "s" : ""} to the marketplace now?`)) return;
    let ok = 0, fail = 0;
    for (const [lid] of ready) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/seller/listings/${lid}/publish`);
        ok += 1;
        setReview((rv) => ({ ...rv, [lid]: { ...rv[lid], status: "approved", can_publish: false, blockers: [] } }));
      } catch { fail += 1; }
    }
    if (fail === 0) toast.success(`${ok} design${ok > 1 ? "s" : ""} published.`);
    else toast.warning(`${ok} published, ${fail} failed — check the queue for details.`);
  };

  const submit = () => submitRows();
  const retryOne = (i) => submitRows([i]);
  const retryAllFailed = () => {
    const idx = rows.map((r, i) => ({ r, i })).filter(({ r }) => r.status === "failed").map(({ i }) => i);
    if (idx.length === 0) return;
    submitRows(idx);
  };
  const cancelBatch = () => {
    if (!window.confirm("Cancel remaining uploads? Files still in the queue will be removed. Files already uploaded will be kept.")) return;
    cancelRef.current = true;
    toast.info("Cancelling — the current file will finish, then the queue stops.");
  };

  const statusChip = (row) => {
    switch (row.status) {
      case "uploading":
        return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-blue-600 bg-blue-500/10 border border-blue-500/30 rounded-full px-2 py-0.5">🔄 {row.pct || 0}%</span>;
      case "processing":
        return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">🔄 rendering</span>;
      case "live":
        return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-emerald-600 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5">✅ complete</span>;
      case "failed":
        return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-red-600 bg-red-500/10 border border-red-500/30 rounded-full px-2 py-0.5">❌ failed</span>;
      case "cancelled":
        return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-muted-foreground bg-secondary border border-border rounded-full px-2 py-0.5">cancelled</span>;
      default:
        return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-muted-foreground bg-secondary border border-border rounded-full px-2 py-0.5">⏳ queued</span>;
    }
  };

  const anyFailed = rows.some((r) => r.status === "failed");
  const allDone = rows.length > 0 && rows.every((r) => ["live", "processing", "cancelled"].includes(r.status));
  const failedRows = rows.map((r, i) => ({ r, i })).filter(({ r }) => r.status === "failed");
  const otherRows = rows.map((r, i) => ({ r, i })).filter(({ r }) => r.status !== "failed");
  const orderedRows = [...failedRows, ...otherRows];

  return (
    <div className="space-y-4" data-testid="seller-bulk">
      <div className="border border-dashed border-border rounded-md p-6 bg-card text-center">
        <UploadCloud className="h-8 w-8 mx-auto text-primary mb-2" />
        <p className="text-sm font-medium">Select multiple .3dm / .stl files at once</p>
        <p className="text-xs text-muted-foreground mb-1">Each file becomes its own listing. We upload and render them <b>one at a time</b> to keep quality high — the queue runs in the background so you can navigate away.</p>
        <p className="text-[11px] text-primary font-mono uppercase tracking-widest mb-3">
          Cap · up to {limits.batch_cap} designs per batch · max {limits.max_file_mb} MB per file
        </p>
        <input type="file" multiple accept=".stl,.3dm" data-testid="bulk-files" onChange={(e) => onFiles(e.target.files)} className="text-sm mx-auto" />
      </div>

      {loading && (
        <div className="border border-primary/30 bg-primary/5 rounded-md p-4" data-testid="bulk-progress-block">
          <div className="flex items-center justify-between text-xs font-mono mb-2">
            <span>{`${phase || "Uploading"} · ${rows.filter((r) => ["live","processing"].includes(r.status)).length + rows.filter((r) => r.status === "failed").length} of ${rows.filter((r) => r.status !== "queued" || processingIdx > -1).length || rows.length} designs`}</span>
            <span data-testid="bulk-progress-pct">{overallPct}% complete</span>
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${overallPct}%` }}
              data-testid="bulk-progress-bar"
            />
          </div>
          <div className="flex items-center justify-between mt-2 gap-2">
            <p className="text-[11px] text-muted-foreground">You can switch tabs — the queue keeps running. Per-file status is below.</p>
            <button
              onClick={cancelBatch}
              data-testid="bulk-cancel"
              className="text-[11px] font-mono uppercase text-red-500 hover:text-red-600 border border-red-500/30 rounded px-2 py-1"
            >
              Cancel remaining
            </button>
          </div>
        </div>
      )}

      {failedRows.length > 0 && !loading && (
        <div className="border border-red-500/40 bg-red-500/5 rounded-md p-3" data-testid="bulk-failed-block">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-mono uppercase tracking-widest text-red-600">
              ❌ Failed uploads · {failedRows.length}
            </p>
            <button
              onClick={retryAllFailed}
              disabled={loading}
              data-testid="bulk-retry-all"
              className="inline-flex items-center gap-1 rounded px-3 py-1.5 border border-red-500/40 text-red-600 text-[11px] font-semibold hover:bg-red-500/10 disabled:opacity-60"
            >
              Retry all failed
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground">These will be re-queued when you click Retry. Others already uploaded are safe.</p>
        </div>
      )}

      {/* 2026-02-28 — Active browser render for the current row in the
          queue. Sequential; only one three.js scene up at a time. */}
      {activeBrowserRender && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="bulk-active-browser-render">
          <p className="text-[11px] font-mono uppercase tracking-widest text-primary mb-2">
            Rendering studio views · {activeBrowserRender.title}
          </p>
          <p className="text-[11px] text-muted-foreground mb-2">
            {browserRenderQueue.length > 0 ? `${browserRenderQueue.length + 1} design${browserRenderQueue.length > 0 ? "s" : ""} in queue.` : "Last one to render."}
          </p>
          <ClientStudioRenderer
            key={activeBrowserRender.listingId}
            listingId={activeBrowserRender.listingId}
            stlUrl={undefined}
            dmUrl={undefined}
            hasStl={activeBrowserRender.hasStl}
            hasDm={activeBrowserRender.hasDm}
            onComplete={() => finishActiveBrowserRender(true)}
            onFailure={() => {
              toast.warning(`"${activeBrowserRender.title}" — browser render failed. Use Fix photos to upload your own.`);
              finishActiveBrowserRender(false);
            }}
          />
        </div>
      )}

      {/* 2026-02 Phase 2 — post-batch review summary. Appears once the
          batch finishes and pollReview() has populated `review` for each
          successfully-uploaded listing. Amber card when some rows still
          need attention; emerald card when everything's ready. */}
      {!loading && phase === "Review" && Object.keys(review).length > 0 && (() => {
        const readyCount = Object.values(review).filter((v) => v.can_publish).length;
        const heldCount = Object.values(review).filter((v) => !v.can_publish && v.status !== "approved").length;
        const liveCount = Object.values(review).filter((v) => v.status === "approved").length;
        const total = Object.keys(review).length;
        const allDoneReview = heldCount === 0;
        return (
          <div
            className={`rounded-md p-4 border ${allDoneReview ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`}
            data-testid="bulk-review-summary"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className={`text-xs font-mono uppercase tracking-widest ${allDoneReview ? "text-emerald-700" : "text-amber-700"}`}>
                  {allDoneReview ? "Batch ready" : "Batch review"}
                </p>
                <p className="text-sm font-semibold mt-1" data-testid="bulk-review-headline">
                  {allDoneReview
                    ? `${readyCount + liveCount} of ${total} designs are ready to publish.`
                    : `${readyCount + liveCount} of ${total} designs ready to publish · ${heldCount} still need attention.`}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {allDoneReview
                    ? "Click Publish all to make them live on the marketplace."
                    : "Rows below with the amber ⚠ badge need you to upload your own render or pick a cover before they can go live."}
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={pollReview}
                  disabled={reviewLoading}
                  data-testid="bulk-review-refresh"
                  className="border border-border rounded-md px-3 py-1.5 text-[11px] font-semibold hover:border-primary disabled:opacity-60 inline-flex items-center gap-1"
                >
                  {reviewLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Refresh
                </button>
                {failedRenderRows().length > 0 && (
                  <button
                    onClick={retryAllFailedRenders}
                    disabled={retryingRenders}
                    data-testid="bulk-retry-failed-renders"
                    title="Re-queue the studio render for every row where auto-render failed. Renders take 20-90 seconds."
                    className="inline-flex items-center gap-1 border border-amber-500/60 bg-amber-500/10 text-amber-800 rounded-md px-3 py-1.5 text-[11px] font-semibold hover:bg-amber-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {retryingRenders ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    Retry all failed renders ({failedRenderRows().length})
                  </button>
                )}
                <button
                  onClick={publishAllReady}
                  disabled={readyCount === 0}
                  data-testid="bulk-publish-all-ready"
                  className="inline-flex items-center gap-1 bg-emerald-600 text-white rounded-md px-3 py-1.5 text-[11px] font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Check className="h-3 w-3" /> Publish all ready ({readyCount})
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {rows.length > 0 && (
        <div className="border border-border rounded-md bg-card overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-secondary/50 text-left"><tr>{["File", "Title", "Category", "Price ₹", "Ring sz", "CAM g", "18K est.", "AI copy", "Progress", ""].map((h) => <th key={h} className="px-2 py-2 font-mono uppercase text-[10px] text-muted-foreground">{h}</th>)}</tr></thead>
            <tbody>
              {orderedRows.map(({ r, i }) => {
                const est = computeWeights(r.cam_weight).gold_18k;
                const disabled = ["live", "processing", "uploading"].includes(r.status);
                const isProcessing = processingIdx === i;
                return (
                  <tr key={r._key || r.filename} className={`border-t border-border ${isProcessing ? "bg-blue-500/5" : r.status === "failed" ? "bg-red-500/5" : ""}`} data-testid={`bulk-row-${i}`}>
                    <td className="px-2 py-1.5 font-mono max-w-[130px]">
                      <div className="truncate" title={r.filename}>{r.filename}</div>
                      <div className="text-[9px] text-muted-foreground">{r.size ? `${(r.size / 1024 / 1024).toFixed(1)} MB` : ""}</div>
                    </td>
                    <td className="px-2 py-1.5"><input value={r.title} onChange={(e) => update(i, "title", e.target.value)} data-testid={`bulk-title-${i}`} className="w-32 border border-border rounded p-1 bg-background" disabled={disabled} /></td>
                    <td className="px-2 py-1.5"><select value={r.category_id} onChange={(e) => update(i, "category_id", e.target.value)} data-testid={`bulk-cat-${i}`} className="w-28 border border-border rounded p-1 bg-background" disabled={disabled}><option value="">—</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></td>
                    <td className="px-2 py-1.5"><input type="number" value={r.price} onChange={(e) => update(i, "price", e.target.value)} data-testid={`bulk-price-${i}`} className="w-20 border border-border rounded p-1 bg-background" disabled={disabled} /></td>
                    <td className="px-2 py-1.5"><input value={r.ring_size} onChange={(e) => update(i, "ring_size", e.target.value)} className="w-12 border border-border rounded p-1 bg-background" disabled={disabled} /></td>
                    <td className="px-2 py-1.5"><input type="number" value={r.cam_weight} onChange={(e) => update(i, "cam_weight", e.target.value)} className="w-16 border border-border rounded p-1 bg-background" disabled={disabled} /></td>
                    <td className="px-2 py-1.5 font-mono" style={{ color: "hsl(var(--gold))" }}>{est ? est + " g" : "—"}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <button type="button" onClick={() => genRow(i)} disabled={r.aiLoading || disabled} data-testid={`bulk-ai-${i}`} title="Generate SEO description & hashtags with AI" className="inline-flex items-center gap-1 rounded px-2 py-1 border text-[10px] font-semibold disabled:opacity-60" style={{ borderColor: "hsl(var(--gold) / 0.5)", color: "hsl(var(--gold))" }}>
                        {r.aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} {r.description ? "Redo" : "AI"}
                      </button>
                      {r.description && <Check className="inline h-3.5 w-3.5 ml-1 text-emerald-500" data-testid={`bulk-ai-done-${i}`} />}
                    </td>
                    <td className="px-2 py-1.5 min-w-[160px]" data-testid={`bulk-status-${i}`}>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between gap-2">
                          {statusChip(r)}
                          <span className="text-[9px] font-mono text-muted-foreground">{r.stage || ""}</span>
                        </div>
                        {(r.status === "uploading" || r.status === "processing") && (
                          <div className="h-1 rounded-full bg-secondary overflow-hidden" data-testid={`bulk-file-progress-${i}`}>
                            <div className="h-full bg-primary transition-all duration-300" style={{ width: `${r.pct || 0}%` }} />
                          </div>
                        )}
                        {r.status === "failed" && r.error && (
                          <span className="text-[10px] text-red-500 max-w-[180px]" title={r.error}>{r.error}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      {r.status === "failed" ? (
                        <button onClick={() => retryOne(i)} disabled={loading} data-testid={`bulk-retry-${i}`} className="inline-flex items-center gap-1 rounded px-2 py-1 border border-primary/40 text-primary text-[10px] font-semibold hover:bg-primary/5 disabled:opacity-60">
                          Retry
                        </button>
                      ) : (r.status === "live" || r.status === "processing") ? (() => {
                        // Phase 2 — show a Fix photos button when the
                        // listing needs manual attention, a Publish
                        // button when it's ready, or an Approved chip
                        // when it's already live.
                        const rv = r.listingId ? review[r.listingId] : null;
                        if (rv && rv.status === "approved") {
                          return <span data-testid={`bulk-live-chip-${i}`} className="text-[10px] font-mono uppercase text-emerald-600">Live ✓</span>;
                        }
                        if (rv && rv.can_publish) {
                          return (
                            <button onClick={() => publishOne(r)} data-testid={`bulk-publish-${i}`} className="inline-flex items-center gap-1 rounded px-2 py-1 border border-emerald-600 bg-emerald-600 text-white text-[10px] font-semibold hover:bg-emerald-700">
                              <Check className="h-3 w-3" /> Publish
                            </button>
                          );
                        }
                        if (rv && rv.blockers.length > 0) {
                          // If the auto-render failed (auto_count===0)
                          // offer both a one-click Retry (re-queues the
                          // studio render) AND the existing Fix photos
                          // button (opens PhotoManager for manual
                          // uploads). Rendering… state disables retry.
                          const renderFailed = rv.auto_count === 0;
                          return (
                            <div className="inline-flex gap-1">
                              {renderFailed && (
                                <button
                                  onClick={() => retryOneRender(r.listingId, r.title)}
                                  disabled={rv._retrying}
                                  data-testid={`bulk-retry-render-${i}`}
                                  title="Retry the studio render for this design"
                                  className="inline-flex items-center gap-1 rounded px-2 py-1 border border-amber-500/60 bg-amber-500/10 text-amber-800 text-[10px] font-semibold hover:bg-amber-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {rv._retrying ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                  {rv._retrying ? "Rendering…" : "Retry"}
                                </button>
                              )}
                              <button onClick={() => onOpenPhotos?.({ id: r.listingId, title: r.title })} data-testid={`bulk-fix-photos-${i}`} title={rv.blockers[0]} className="inline-flex items-center gap-1 rounded px-2 py-1 border border-amber-500/50 text-amber-700 bg-amber-500/10 text-[10px] font-semibold hover:bg-amber-500/20">
                                <UploadCloud className="h-3 w-3" /> Fix photos
                              </button>
                            </div>
                          );
                        }
                        return <span className="text-[10px] text-muted-foreground font-mono">—</span>;
                      })() : r.status === "cancelled" ? (
                        <button onClick={() => remove(i)} className="text-red-500" data-testid={`bulk-remove-${i}`} title="Remove"><Trash2 className="h-4 w-4" /></button>
                      ) : r.status === "uploading" ? (
                        <span className="text-[10px] text-muted-foreground font-mono">—</span>
                      ) : (
                        <button onClick={() => remove(i)} className="text-red-500" data-testid={`bulk-remove-${i}`} title="Remove from queue"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={genAll} disabled={aiAllLoading || loading} data-testid="bulk-ai-all" className="flex items-center gap-2 rounded-md px-4 py-2.5 font-semibold border disabled:opacity-60" style={{ borderColor: "hsl(var(--gold) / 0.5)", color: "hsl(var(--gold))" }}>{aiAllLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate all with AI</button>
          {!allDone && (
            <button onClick={submit} disabled={loading} data-testid="submit-bulk" className="flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-5 py-2.5 font-semibold disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Submit {rows.filter((r) => r.status === "queued" || r.status === "failed").length} designs
            </button>
          )}
          {allDone && (
            <button onClick={onDone} data-testid="bulk-view-listings" className="flex items-center gap-2 bg-emerald-600 text-white rounded-md px-5 py-2.5 font-semibold hover:bg-emerald-700">
              <Check className="h-4 w-4" /> View my listings
            </button>
          )}
          {anyFailed && !loading && (
            <span className="text-xs text-red-500 font-mono" data-testid="bulk-any-failed">{failedRows.length} failed — Retry above.</span>
          )}
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <input type={type} value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)} required={label === "Title" || label.startsWith("Price")} className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background focus:outline-none focus:border-primary" />
    </label>
  );
}

function RouteOnboardingCard() {
  const [status, setStatus] = useState(null); // null=loading, {onboarded:bool,...}

  useEffect(() => {
    api.get("/seller/razorpay/status").then((r) => setStatus(r.data)).catch(() => setStatus({ onboarded: false }));
  }, []);

  if (status === null) {
    return (
      <div className="border border-border rounded-md bg-card p-5 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking Razorpay Route status…
      </div>
    );
  }

  if (status.onboarded) {
    const active = status.activated || status.status === "activated";
    return (
      <div className="border border-emerald-500/40 bg-emerald-500/5 rounded-md p-5" data-testid="route-status-card">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-8 w-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <Check className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="flex-1">
            <p className="font-display font-semibold text-sm">Razorpay Route connected</p>
            <p className="text-xs text-muted-foreground">
              {active ? "New sales settle to your linked bank account automatically (after the escrow hold)." : `Status: ${status.status || "under_review"} — complete KYC in the Razorpay Dashboard to activate.`}
            </p>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-emerald-500/20 text-emerald-700">
            {active ? "auto-payouts" : "kyc pending"}
          </span>
        </div>
        <p className="mt-2 text-[11px] font-mono text-muted-foreground">
          Linked account: <span className="text-foreground">{status.linked_account_id}</span>
        </p>
      </div>
    );
  }

  // 2026-09 — Route onboarding is parked. The form is replaced by a
  // read-only "coming soon" card until auto-payouts go live.
  return (
    <div className="border border-border rounded-md bg-secondary/40 p-5" data-testid="route-coming-soon-card">
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
          <Banknote className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-semibold text-sm">Automatic payouts</p>
            <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-amber-500/15 text-amber-700 border border-amber-500/30" data-testid="route-coming-soon-badge">
              Coming soon
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Auto-settlement to your bank account is being set up and isn't open for sign-up yet. Until then, request a payout below — we process manual withdrawals to your verified bank account or UPI.
          </p>
        </div>
      </div>
    </div>
  );
}

function PayoutTab({ wallet, payouts, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState(wallet?.tds_preview ? "bank" : "upi");
  const gated = !wallet?.can_withdraw;
  const kycBlocked = wallet?.blocking_reason?.startsWith("kyc_");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/seller/payouts", { amount: Number(amount), method });
      toast.success("Payout requested"); setAmount(""); onDone();
    } catch (e2) { toast.error(apiErr(e2)); }
  };
  // TDS math for what the user is about to withdraw
  const tds = wallet?.tds_preview;
  const enteredAmt = Number(amount) || 0;
  const wouldDeduct = tds && enteredAmt > 0
    ? (tds.ytd_gross + enteredAmt <= tds.exemption_inr ? 0 : +(enteredAmt * (tds.rate_pct / 100)).toFixed(2))
    : 0;
  const netAfter = +(enteredAmt - wouldDeduct).toFixed(2);
  return (
    <div className="space-y-6">
      {kycBlocked && (
        <div data-testid="kyc-gate-banner" className="border border-amber-300 bg-amber-50 text-amber-900 rounded-md p-4 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <p className="font-semibold">
              {inr(wallet?.available || 0)} available. Complete KYC to withdraw.
            </p>
            <p className="mt-1 text-amber-800">
              Under Section 194-O we need your PAN and a verified bank account or UPI ID before releasing any payout. Earnings continue accruing while you complete this — nothing is lost.
            </p>
            <button data-testid="go-to-kyc" onClick={() => onDone("kyc")} className="mt-3 bg-amber-900 text-white rounded-md px-4 py-2 text-xs font-semibold hover:bg-amber-800">
              Complete KYC now →
            </button>
          </div>
        </div>
      )}
      <RouteOnboardingCard />
      <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={submit} className={`border border-border rounded-md bg-card p-6 space-y-4 h-fit ${gated ? "opacity-60" : ""}`}>
        <h2 className="font-display font-semibold text-lg">Request a payout</h2>
        <p className="text-sm text-muted-foreground">Available: <span className="font-mono font-semibold text-emerald-600">{inr(wallet?.available || 0)}</span></p>
        <Input label="Amount (INR)" type="number" value={amount} onChange={setAmount} testid="payout-amount" />
        <div>
          <label className="text-sm font-medium">Method</label>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background"><option value="upi">UPI</option><option value="bank">Bank transfer</option></select>
        </div>
        {tds && enteredAmt > 0 && (
          <div data-testid="tds-preview" className="rounded-md border border-border bg-secondary/40 p-3 text-xs space-y-1">
            <p className="flex justify-between"><span>Gross withdrawal</span><span className="font-mono">{inr(enteredAmt)}</span></p>
            <p className="flex justify-between text-amber-700"><span>TDS 194-O @ {tds.rate_pct}%</span><span className="font-mono">{wouldDeduct > 0 ? `-${inr(wouldDeduct)}` : "₹0 (below ₹5k FY exemption)"}</span></p>
            <p className="flex justify-between font-semibold border-t border-border pt-1"><span>Net you receive</span><span className="font-mono text-emerald-600">{inr(netAfter)}</span></p>
          </div>
        )}
        <button
          data-testid="request-payout"
          disabled={gated}
          title={gated ? "Complete KYC to enable withdrawal" : ""}
          className="bg-primary text-primary-foreground rounded-md px-5 py-2.5 font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
          {gated ? "Withdrawal disabled" : "Request payout"}
        </button>
      </form>
      <div className="border border-border rounded-md bg-card overflow-hidden h-fit">
        <p className="px-4 py-3 font-display font-semibold text-sm border-b border-border">Payout history</p>
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left"><tr>{["Gross", "TDS", "Net", "Method", "Date", "Status"].map((h) => <th key={h} className="px-4 py-2.5 text-[11px] uppercase font-mono text-muted-foreground">{h}</th>)}</tr></thead>
          <tbody>
            {payouts.map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="px-4 py-2.5 font-mono">{inr(p.amount)}</td>
                <td className="px-4 py-2.5 font-mono text-amber-700">{p.tds_amount ? `-${inr(p.tds_amount)}` : "—"}</td>
                <td className="px-4 py-2.5 font-mono font-semibold text-emerald-700">{inr(p.net_amount || p.amount)}</td>
                <td className="px-4 py-2.5 uppercase text-xs">{p.method}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-2.5"><StatusTag status={p.status} /></td>
              </tr>
            ))}
            {payouts.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No payouts yet.</td></tr>}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

function KycTab({ onDone }) {
  const { user } = useAuth();
  // 2026-03-01 — International sellers detected by phone country prefix.
  // Non-+91 numbers use the SWIFT/BIC + generic tax-ID path and always
  // land in admin manual-review (no automated cross-border verifier).
  const phoneDigits = (user?.phone || "").replace(/\D/g, "");
  const isIntl = phoneDigits.length > 10 && !phoneDigits.startsWith("91");
  const country = isIntl ? "INTL" : "IN";
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(1);      // 1=pan, 2=bank, 3=upi (either 2 or 3 unlocks payout)
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(null);   // fuzzy-match awaiting confirmation
  const refresh = () => api.get("/seller/kyc/status").then((r) => {
    setStatus(r.data);
    if (r.data.status === "verified") setStep(4);
    else if (r.data.profile.pan_captured_at && !r.data.profile.bank) setStep(2);
    else if (r.data.profile.pan_captured_at && r.data.profile.bank && !r.data.profile.upi) setStep(3);
  });
  useEffect(() => { refresh(); }, []);
  if (!status) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const StepHead = ({ n, label, done }) => (
    <div className={`flex items-center gap-3 ${done ? "text-emerald-700" : step === n ? "text-primary" : "text-muted-foreground"}`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${done ? "bg-emerald-100" : step === n ? "bg-primary/10" : "bg-secondary"}`}>
        {done ? <Check className="w-3 h-3" /> : n}
      </div>
      <span className="text-sm font-medium">{label}</span>
    </div>
  );

  return (
    <div className="max-w-2xl space-y-6">
      {isIntl && (
        <div className="border border-blue-300 bg-blue-50 text-blue-900 rounded-md p-4" data-testid="kyc-intl-banner">
          <p className="font-semibold text-sm">International seller flow</p>
          <p className="text-xs mt-1">Because your phone country code is <span className="font-mono">+{phoneDigits.slice(0, phoneDigits.length - 10) || "??"}</span>, we'll capture your local tax ID + bank SWIFT/BIC and route your KYC to our team for manual review. Approvals typically land within 24 hours.</p>
        </div>
      )}
      <div className="border border-border rounded-md bg-card p-4">
        <p className="text-xs text-muted-foreground mb-3">Verification steps</p>
        <div className="flex flex-col gap-3">
          <StepHead n={1} label={isIntl ? "Tax ID & personal details" : "PAN & personal details"} done={!!status.profile.pan_captured_at} />
          <StepHead n={2} label={isIntl ? "Bank account (SWIFT/BIC)" : "Bank account (penny-drop verified)"} done={!!status.profile.bank} />
          {!isIntl && <StepHead n={3} label="UPI ID (required)" done={!!status.profile.upi} />}
        </div>
      </div>

      {status.status === "verified" && (
        <div data-testid="kyc-verified-banner" className="border border-emerald-300 bg-emerald-50 text-emerald-900 rounded-md p-4">
          <p className="font-semibold text-sm">KYC verified ✓</p>
          <p className="text-xs mt-1">You can now withdraw earnings from the Payouts tab.</p>
        </div>
      )}

      {(status.profile.bank || status.profile.upi) && (
        <div className="border border-border rounded-md bg-card p-4 text-sm space-y-2" data-testid="kyc-linked-payout">
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Linked payout destination</p>
          {status.profile.bank && (
            <div className="space-y-1">
              <p className="font-semibold" data-testid="kyc-linked-bank-name">
                {status.profile.bank.bank_name || "Bank account"}
                {status.profile.bank.bank_branch ? <span className="font-normal text-muted-foreground"> — {status.profile.bank.bank_branch}</span> : null}
              </p>
              <p className="text-xs text-muted-foreground font-mono">
                A/c XXXX XXXX {status.profile.bank.last4 || "----"}
                {status.profile.bank.ifsc ? ` · ${status.profile.bank.ifsc}` : ""}
              </p>
              {status.profile.bank.registered_name && (
                <p className="text-xs text-muted-foreground">Holder: {status.profile.bank.registered_name}</p>
              )}
            </div>
          )}
          {status.profile.upi && (
            <p className="text-xs text-muted-foreground font-mono" data-testid="kyc-linked-upi">UPI · {status.profile.upi.masked_vpa}</p>
          )}
        </div>
      )}

      {status.status === "manual_review" && (
        <div className="border border-amber-300 bg-amber-50 text-amber-900 rounded-md p-4 text-sm">
          <p className="font-semibold">Manual review in progress</p>
          <p className="text-xs mt-1">Automatic verification didn't succeed after several attempts. Our team will review within 24 hours. Contact <a href="mailto:support@jeweltix.com" className="underline">support@jeweltix.com</a> to expedite.</p>
        </div>
      )}

      {step === 1 && <KycPanStep country={country} isIntl={isIntl} onNext={async () => { await refresh(); setStep(2); }} />}
      {step === 2 && (
        <KycBankStep
          country={country}
          isIntl={isIntl}
          rateLimit={status.rate_limit}
          pending={pending}
          setPending={setPending}
          saving={saving}
          setSaving={setSaving}
          onNext={async () => { await refresh(); if (isIntl) { if (onDone) onDone(); } else setStep(3); }}
        />
      )}
      {step === 3 && !isIntl && (
        <KycUpiStep
          rateLimit={status.rate_limit}
          pending={pending}
          setPending={setPending}
          saving={saving}
          setSaving={setSaving}
          onNext={async () => { await refresh(); if (onDone) onDone(); }}
        />
      )}

      {status.total_failures > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="kyc-failure-count">
          You can retry verification as many times as you need — there's no daily limit.
          Failed attempts so far: {status.total_failures} / {status.manual_review_threshold} (at that point our team reviews it manually for you).
        </p>
      )}
    </div>
  );
}

function KycPanStep({ country = "IN", isIntl = false, onNext }) {
  const [f, setF] = useState({ pan: "", tax_id_type: isIntl ? "TRN" : "PAN", full_name: "", dob: "", account_type: "individual",
                                business_name: "", business_address: "", consent: false });
  const [busy, setBusy] = useState(false);
  // 2026-03-01 — PAN card image upload (spec §5). Required for Indian
  // sellers; the image lives in R2 with server-side encryption + audit
  // logging on every admin download. State: `uploaded` reflects the
  // server's `/pan-image/status`. `pendingImage` holds the local File
  // + preview URL between selection and upload.
  const [uploaded, setUploaded] = useState(null);   // { content_type, uploaded_at, size_bytes } | null
  const [pendingImage, setPendingImage] = useState(null); // { file, previewUrl }
  const [uploadingImg, setUploadingImg] = useState(false);
  useEffect(() => {
    if (isIntl) return;
    api.get("/seller/kyc/pan-image/status").then((r) => setUploaded(r.data?.uploaded ? r.data : null)).catch(() => setUploaded(null));
  }, [isIntl]);
  const onImgPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^(image\/(jpeg|png|jpg)|application\/pdf)$/i.test(file.type)) {
      toast.error("Please pick a JPG, PNG or PDF file.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Please keep the file under 8 MB.");
      return;
    }
    // Preview URL for JPG/PNG; PDF just shows a filename card.
    const previewUrl = file.type === "application/pdf" ? null : URL.createObjectURL(file);
    setPendingImage({ file, previewUrl });
  };
  const uploadImg = async () => {
    if (!pendingImage) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingImage.file, pendingImage.file.name);
      await api.post("/seller/kyc/pan-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PAN card uploaded ✓");
      const { data } = await api.get("/seller/kyc/pan-image/status");
      setUploaded(data?.uploaded ? data : null);
      setPendingImage(null);
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setUploadingImg(false); }
  };
  // Indian PAN regex only enforced when we're in the Indian flow.
  const panOk = isIntl
    ? (f.pan || "").replace(/[-\s]/g, "").length >= 2
    : /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(f.pan.toUpperCase());
  const expected4th = ({
    individual: "P", proprietorship: "P", partnership: "F", private_limited: "C",
  })[f.account_type];
  const panFourthOk = isIntl || !panOk || !expected4th || f.pan.toUpperCase()[3] === expected4th;
  // Image upload is mandatory for Indian sellers, optional for
  // international (they can't rely on Indian PAN semantics).
  const imgReq = !isIntl;
  const imgOk = !imgReq || !!uploaded;
  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) return toast.error("Please tick the consent checkbox — it's required under DPDP Act 2023.");
    if (!panFourthOk) {
      return toast.error(`The 4th character of your PAN (${f.pan.toUpperCase()[3]}) doesn't match the account type. Expected '${expected4th}'.`);
    }
    if (!imgOk) return toast.error("Please upload a clear photo of your PAN card first.");
    setBusy(true);
    try {
      await api.post("/seller/kyc/pan", {
        ...f,
        country,
        pan: isIntl ? f.pan.trim() : f.pan.toUpperCase(),
      });
      toast.success(isIntl ? "Tax ID saved — moving to bank details" : "PAN details saved");
      onNext();
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="border border-border rounded-md bg-card p-6 space-y-4">
      <h2 className="font-display font-semibold text-lg">Step 1 — {isIntl ? "Tax ID & personal details" : "PAN & personal details"}</h2>
      <p className="text-xs text-muted-foreground">{isIntl ? "Your local tax ID is captured for compliance. An admin will verify manually within 24 hours." : "Your PAN is required for TDS compliance on payouts under Section 194-O."}</p>
      {isIntl && (
        <label className="block">
          <span className="text-sm font-medium">Tax ID type</span>
          <select
            value={f.tax_id_type}
            onChange={(e) => setF({ ...f, tax_id_type: e.target.value })}
            data-testid="kyc-tax-id-type"
            className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background"
          >
            <option value="TRN">TRN / VAT number</option>
            <option value="CNIC">CNIC (Pakistan)</option>
            <option value="EIN">EIN (US)</option>
            <option value="SSN">SSN (US)</option>
            <option value="VAT">VAT ID (EU / UK)</option>
            <option value="TIN">TIN (Turkey / other)</option>
            <option value="OTHER">Other</option>
          </select>
        </label>
      )}
      <Input label={isIntl ? "Tax ID number" : "PAN number"} value={f.pan} onChange={(v) => setF({ ...f, pan: isIntl ? v.slice(0, 30) : v.toUpperCase().slice(0, 10) })} testid="kyc-pan-input" />
      {!isIntl && f.pan && !panOk && <p className="text-xs text-rose-600 -mt-2">Format: 5 letters + 4 digits + 1 letter (e.g. ABCPK1234E)</p>}
      {!isIntl && panOk && !panFourthOk && <p className="text-xs text-rose-600 -mt-2">The 4th character of your PAN is <span className="font-mono font-bold">{f.pan.toUpperCase()[3]}</span> — for a {f.account_type.replace("_", " ")} account we expect <span className="font-mono font-bold">{expected4th}</span>. Please re-check the PAN or the account type.</p>}
      <Input label={isIntl ? "Full legal name (as on tax records)" : "Full name (exactly as on PAN)"} value={f.full_name} onChange={(v) => setF({ ...f, full_name: v })} testid="kyc-fullname" />
      <Input label="Date of birth / incorporation" type="date" value={f.dob} onChange={(v) => setF({ ...f, dob: v })} testid="kyc-dob" />
      <div>
        <label className="text-sm font-medium">Account type</label>
        <select value={f.account_type} onChange={(e) => setF({ ...f, account_type: e.target.value })} className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background" data-testid="kyc-account-type">
          <option value="individual">Individual</option>
          <option value="proprietorship">Proprietorship</option>
          <option value="partnership">Partnership</option>
          <option value="private_limited">Private Limited</option>
        </select>
      </div>
      {f.account_type !== "individual" && (
        <>
          <Input label="Business name" value={f.business_name} onChange={(v) => setF({ ...f, business_name: v })} testid="kyc-bizname" />
          <Input label="Business address" value={f.business_address} onChange={(v) => setF({ ...f, business_address: v })} testid="kyc-bizaddr" />
        </>
      )}
      {/* 2026-03-01 — GSTIN input removed per spec §3. */}
      {!isIntl && (
        <div className="border border-dashed border-border rounded-md p-4 space-y-3 bg-secondary/30" data-testid="pan-image-block">
          <div>
            <p className="text-sm font-medium">PAN card image</p>
            <p className="text-xs text-muted-foreground mt-0.5">Upload a clear photo of your PAN card. All four corners visible, text readable, no glare. JPG / PNG / PDF, max 8 MB.</p>
          </div>
          {uploaded ? (
            <div className="flex items-center justify-between gap-3 border border-emerald-300 bg-emerald-50 rounded-md p-3">
              <div className="text-xs">
                <p className="font-semibold text-emerald-900">✓ PAN card uploaded</p>
                <p className="text-emerald-800/70 mt-0.5">{uploaded.content_type} · {Math.round((uploaded.size_bytes || 0) / 1024)} KB · {new Date(uploaded.uploaded_at).toLocaleDateString()}</p>
              </div>
              <button
                type="button"
                onClick={() => { setUploaded(null); setPendingImage(null); }}
                data-testid="pan-image-replace"
                className="text-xs border border-border rounded-md px-3 py-1.5 bg-white hover:bg-secondary"
              >
                Replace
              </button>
            </div>
          ) : pendingImage ? (
            <div className="space-y-2">
              {pendingImage.previewUrl ? (
                <img src={pendingImage.previewUrl} alt="PAN preview" className="w-full max-h-64 object-contain rounded-md border border-border bg-black/5" data-testid="pan-image-preview" />
              ) : (
                <div className="border border-border rounded-md p-3 text-xs font-mono bg-white">{pendingImage.file.name} ({Math.round(pendingImage.file.size / 1024)} KB)</div>
              )}
              <div className="flex gap-2">
                <button type="button" onClick={uploadImg} disabled={uploadingImg} data-testid="pan-image-upload-btn" className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50">
                  {uploadingImg ? "Uploading…" : "Upload PAN card"}
                </button>
                <button type="button" onClick={() => setPendingImage(null)} className="border border-border rounded-md px-3 py-2 text-sm hover:bg-secondary">Choose another</button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <label className="flex-1 border border-border rounded-md px-4 py-3 text-sm text-center cursor-pointer hover:bg-secondary" data-testid="pan-image-pick-file">
                <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={onImgPick} className="hidden" />
                Pick from files
              </label>
              <label className="flex-1 border border-border rounded-md px-4 py-3 text-sm text-center cursor-pointer hover:bg-secondary sm:hidden" data-testid="pan-image-camera">
                <input type="file" accept="image/*" capture="environment" onChange={onImgPick} className="hidden" />
                Take a photo
              </label>
            </div>
          )}
          <p className="text-[10px] text-muted-foreground">🔒 Encrypted at rest · viewable only by our Finance team after 2FA · never shared.</p>
        </div>
      )}
      <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
        <input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} data-testid="kyc-consent" />
        <span>I consent to Jeweltix collecting and storing my tax ID, bank and identity details for tax compliance and payout processing purposes, in line with the Digital Personal Data Protection Act, 2023.</span>
      </label>
      <button disabled={busy || !panOk || !panFourthOk || !f.full_name || !f.dob || !f.consent || !imgOk} data-testid="kyc-submit-pan" className="bg-primary text-primary-foreground rounded-md px-5 py-2.5 font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
        {busy ? "Saving…" : "Save & continue →"}
      </button>
    </form>
  );
}

function KycBankStep({ country = "IN", isIntl = false, rateLimit, pending, setPending, saving, setSaving, onNext }) {
  const [f, setF] = useState({
    account_holder_name: "", account_number: "", account_number_confirm: "",
    ifsc: "", swift_code: "", bank_name: "", bank_country: isIntl ? "" : "IN",
  });
  const [ifscInfo, setIfscInfo] = useState(null);   // { bank_name, branch, city } | "notfound" | null
  useEffect(() => {
    if (isIntl) return;
    const code = f.ifsc.toUpperCase();
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(code)) { setIfscInfo(null); return; }
    let dead = false;
    api.get(`/seller/kyc/ifsc/${code}`)
      .then((r) => { if (!dead) setIfscInfo(r.data.found ? r.data : "notfound"); })
      .catch(() => { if (!dead) setIfscInfo("notfound"); });
    return () => { dead = true; };
  }, [f.ifsc, isIntl]);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/seller/kyc/bank", {
        ...f,
        country,
        ifsc: isIntl ? null : f.ifsc.toUpperCase(),
        swift_code: isIntl ? f.swift_code.toUpperCase() : null,
        bank_name: isIntl ? f.bank_name : (ifscInfo && ifscInfo !== "notfound" ? ifscInfo.bank_name : null),
        bank_country: isIntl ? (f.bank_country || country) : null,
      });
      if (data.needs_manual_review) {
        toast.success(data.message || "Bank details saved — admin review pending");
        setPending(null);
        onNext();
      } else if (data.needs_confirmation) {
        setPending({ kind: "bank", ...data });
        toast.info(`Bank shows "${data.result.registered_name}" — please confirm this is you.`);
      } else if (data.ok) {
        toast.success("Bank verified ✓");
        setPending(null);
        onNext();
      } else {
        toast.error(data.result?.reason || "Verification failed");
      }
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setSaving(false); }
  };
  const confirm = async (accept) => {
    setSaving(true);
    try {
      await api.post("/seller/kyc/confirm-name-match", { kind: "bank", method_id: pending.method_id, accept });
      if (accept) { toast.success("Bank account verified ✓"); onNext(); }
      else { toast.info("Rejected — please try again with correct details."); }
      setPending(null);
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="border border-border rounded-md bg-card p-6 space-y-4">
      <h2 className="font-display font-semibold text-lg">Step 2 — Bank account</h2>
      <p className="text-xs text-muted-foreground">
        {isIntl
          ? "We record your bank details and forward them to our team for manual verification. Payouts unlock after admin approval."
          : "We deposit ₹1 to verify the account holder name. Made a typo? Just correct it and try again — retries are unlimited."
        }
      </p>
      <Input label="Account holder name" value={f.account_holder_name} onChange={(v) => setF({ ...f, account_holder_name: v })} testid="kyc-bank-name" />
      <Input label={isIntl ? "Account number / IBAN" : "Account number"} value={f.account_number} onChange={(v) => setF({ ...f, account_number: isIntl ? v.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 40) : v.replace(/\D/g, "") })} testid="kyc-bank-acct" />
      <div>
        <label className="text-sm font-medium">Re-enter {isIntl ? "account number / IBAN" : "account number"}</label>
        <input
          value={f.account_number_confirm}
          onChange={(e) => setF({ ...f, account_number_confirm: isIntl ? e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 40) : e.target.value.replace(/\D/g, "") })}
          onPaste={(e) => { e.preventDefault(); toast.warning("Please type the account number — paste is disabled for safety."); }}
          className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background font-mono"
          data-testid="kyc-bank-acct-confirm"
        />
        {f.account_number_confirm && f.account_number !== f.account_number_confirm && (
          <p className="text-xs text-rose-600 mt-1">Account numbers don't match.</p>
        )}
      </div>
      {isIntl ? (
        <>
          <Input label="SWIFT / BIC code" value={f.swift_code} onChange={(v) => setF({ ...f, swift_code: v.toUpperCase().slice(0, 11) })} testid="kyc-bank-swift" />
          <Input label="Bank name" value={f.bank_name} onChange={(v) => setF({ ...f, bank_name: v })} testid="kyc-bank-name-field" />
          <Input label="Bank country (2-letter code, e.g. PK, AE, TR)" value={f.bank_country} onChange={(v) => setF({ ...f, bank_country: v.toUpperCase().slice(0, 2) })} testid="kyc-bank-country" />
        </>
      ) : (
        <>
          <Input label="IFSC code" value={f.ifsc} onChange={(v) => setF({ ...f, ifsc: v.toUpperCase().slice(0, 11) })} testid="kyc-bank-ifsc" />
          {ifscInfo && ifscInfo !== "notfound" && (
            <div className="border border-emerald-300 bg-emerald-50 rounded-md px-3 py-2 text-xs text-emerald-900" data-testid="kyc-bank-resolved">
              <span className="font-semibold">{ifscInfo.bank_name}</span>
              {ifscInfo.branch ? <> — {ifscInfo.branch}</> : null}
              {ifscInfo.city ? <span className="text-emerald-700/80">, {ifscInfo.city}</span> : null}
            </div>
          )}
          {ifscInfo === "notfound" && (
            <p className="text-xs text-amber-700" data-testid="kyc-bank-ifsc-unknown">We couldn't look up this IFSC automatically — double-check it. You can still continue; our team will confirm the bank.</p>
          )}
        </>
      )}
      {pending && pending.kind === "bank" && (
        <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm space-y-2" data-testid="bank-name-match-card">
          <p className="font-semibold text-amber-900">Please confirm the account holder</p>
          <div className="text-xs bg-white/70 rounded p-2 space-y-1 border border-amber-200/70">
            <div className="flex justify-between"><span className="text-amber-800/70">Your PAN name:</span> <span className="font-mono font-semibold">{f.account_holder_name || "—"}</span></div>
            <div className="flex justify-between"><span className="text-amber-800/70">Bank shows:</span> <span className="font-mono font-semibold">{pending.result.registered_name}</span></div>
            <div className="flex justify-between"><span className="text-amber-800/70">Match score:</span> <span className="font-mono font-semibold">{pending.result.name_match}/100</span></div>
          </div>
          <p className="text-xs text-amber-800">If the two names refer to the same person (initials, middle name, ordering) confirm — otherwise please use a bank account in your own name.</p>
          <div className="flex gap-2 mt-2">
            <button type="button" data-testid="kyc-bank-confirm-yes" onClick={() => confirm(true)} disabled={saving} className="bg-emerald-600 text-white rounded-md px-4 py-1.5 text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50">Yes, same person</button>
            <button type="button" data-testid="kyc-bank-confirm-no" onClick={() => confirm(false)} disabled={saving} className="bg-slate-200 rounded-md px-4 py-1.5 text-xs font-semibold hover:bg-slate-300 disabled:opacity-50">No, retry</button>
          </div>
        </div>
      )}
      <button disabled={saving || !!pending} data-testid="kyc-submit-bank" className="bg-primary text-primary-foreground rounded-md px-5 py-2.5 font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
        {saving ? <span className="inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Verifying with your bank…</span> : "Verify bank account"}
      </button>
    </form>
  );
}

function KycUpiStep({ rateLimit, pending, setPending, saving, setSaving, onNext }) {
  const [f, setF] = useState({ upi_id: "", account_holder_name: "" });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/seller/kyc/upi", f);
      if (data.needs_confirmation) {
        setPending({ kind: "upi", ...data });
      } else if (data.ok) {
        toast.success("UPI verified ✓"); setPending(null); onNext();
      } else {
        toast.error(data.result?.reason || "Verification failed");
      }
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setSaving(false); }
  };
  const confirm = async (accept) => {
    setSaving(true);
    try {
      await api.post("/seller/kyc/confirm-name-match", { kind: "upi", method_id: pending.method_id, accept });
      if (accept) { toast.success("UPI verified ✓"); onNext(); } else toast.info("Rejected — please retry.");
      setPending(null);
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="border border-border rounded-md bg-card p-6 space-y-4">
      <h2 className="font-display font-semibold text-lg">Step 3 — UPI ID (required)</h2>
      <p className="text-xs text-muted-foreground">A verified UPI ID is required to complete KYC and unlock withdrawals. Wrong ID? Correct it and try again — retries are unlimited.</p>
      <Input label="UPI ID (name@bank)" value={f.upi_id} onChange={(v) => setF({ ...f, upi_id: v.trim() })} testid="kyc-upi-id" />
      <Input label="Account holder name" value={f.account_holder_name} onChange={(v) => setF({ ...f, account_holder_name: v })} testid="kyc-upi-name" />
      {pending && pending.kind === "upi" && (
        <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm space-y-2" data-testid="upi-name-match-card">
          <p className="font-semibold text-amber-900">Please confirm the UPI holder</p>
          <div className="text-xs bg-white/70 rounded p-2 space-y-1 border border-amber-200/70">
            <div className="flex justify-between"><span className="text-amber-800/70">Your PAN name:</span> <span className="font-mono font-semibold">{f.account_holder_name || "—"}</span></div>
            <div className="flex justify-between"><span className="text-amber-800/70">UPI shows:</span> <span className="font-mono font-semibold">{pending.result.registered_name}</span></div>
            <div className="flex justify-between"><span className="text-amber-800/70">Match score:</span> <span className="font-mono font-semibold">{pending.result.name_match}/100</span></div>
          </div>
          <p className="text-xs text-amber-800">If the two names refer to the same person (initials, middle name, ordering) confirm — otherwise please use a UPI ID in your own name.</p>
          <div className="flex gap-2 mt-2">
            <button type="button" data-testid="kyc-upi-confirm-yes" onClick={() => confirm(true)} disabled={saving} className="bg-emerald-600 text-white rounded-md px-4 py-1.5 text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50">Yes, same person</button>
            <button type="button" data-testid="kyc-upi-confirm-no" onClick={() => confirm(false)} disabled={saving} className="bg-slate-200 rounded-md px-4 py-1.5 text-xs font-semibold hover:bg-slate-300 disabled:opacity-50">No, retry</button>
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <button disabled={saving || !!pending} data-testid="kyc-submit-upi" className="bg-primary text-primary-foreground rounded-md px-5 py-2.5 font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? <span className="inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Verifying…</span> : "Verify UPI"}
        </button>
      </div>
    </form>
  );
}


// 2026-03-01 — Self-service appeal form for auto-suspended sellers.
// Modal-only, minimum 30 chars, single pending appeal at a time.
function AppealModal({ suspension, onClose, onSubmitted }) {
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const MIN = 30;
  const MAX = 3000;
  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < MIN) {
      toast.error(`Please write at least ${MIN} characters explaining what happened.`);
      return;
    }
    setSaving(true);
    try {
      await api.post("/seller/appeal", { message: message.trim() });
      toast.success("Appeal submitted — we'll review within 2 business days.");
      onSubmitted();
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-[9998] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="appeal-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Appeal your suspension</h3>
          <button onClick={onClose} data-testid="appeal-close" className="text-muted-foreground hover:text-foreground text-xl leading-none">&times;</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="text-sm text-slate-700 space-y-2">
            <p>Tell us what happened — for example:</p>
            <ul className="list-disc pl-5 space-y-0.5 text-xs text-slate-600">
              <li>The photos were correct renders of the CAD; the match check made an error.</li>
              <li>You uploaded the wrong photos by mistake and have since fixed them.</li>
              <li>Any context you'd like our team to consider when reviewing.</li>
            </ul>
          </div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX))}
            rows={7}
            data-testid="appeal-message"
            placeholder="Explain what happened…"
            className="w-full border border-border rounded-md p-3 text-sm bg-background focus:outline-none focus:border-primary resize-y"
          />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{message.trim().length < MIN ? `${MIN - message.trim().length} more characters needed` : "Ready to submit"}</span>
            <span>{message.length}/{MAX}</span>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button type="button" onClick={onClose} className="border border-border rounded-md px-4 py-2 text-sm hover:bg-secondary" data-testid="appeal-cancel">Cancel</button>
            <button
              type="submit"
              disabled={saving || message.trim().length < MIN}
              data-testid="appeal-submit"
              className="bg-primary text-primary-foreground rounded-md px-5 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <span className="inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Submitting…</span> : "Submit appeal"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
